import {
  ALL_REVIEW_STATUSES,
  finalOutputStep,
  isReviewStatus,
  REVIEW_STATUS_META,
  type JobStatus,
  type ReviewStatus,
} from "@zenforge/shared";
import { useEffect, useRef, useState } from "react";
import { api, useApi, usePendingActions } from "../api.js";
import { usePermission } from "../auth-context.js";
import { ContentPreview, type ResponseType } from "../ContentPreview.js";
import { ContentReviewSection, ReviewControl } from "../ContentReviewSection.js";
import { PdfViewerModal } from "../PdfViewerModal.js";
import { SourcedContentView, type ContentSource } from "../SourcedContentView.js";
import { DocxViewerModal } from "../DocxViewerModal.js";
import {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZES,
  apiQueryFromFilters,
  initialFilters,
  persistFilters,
  searchFromFilters,
  severityOf,
  queueApiQueryFromFilters,
  queueInitialFilters,
  queuePersistFilters,
  queueSearchFromFilters,
  type ExecutionFilters,
  type QueueFilters,
  type TimeRange,
} from "./execution-filters.js";
import {
  ActionButton,
  FullScreenLoader,
  Notice,
  RefetchOverlay,
  StatusBadge,
  StatusDot,
  compactCost,
  compactTokens,
  formatCost,
  formatDuration,
  statusText,
  useToast,
} from "../components.js";

interface JobRow {
  id: number;
  jobType: "generate" | "critique";
  status: JobStatus;
  attemptCount: number;
  priority: number;
  model: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  /** Both null on jobs predating migration 021 — render as unknown, not 0. */
  cachedTokens: number | null;
  searchCalls: number | null;
  estimatedCost: string | number | null;
  /** Provider-measured call time; null on jobs that predate migration 018 or
   *  have not finished yet. */
  durationMs: number | null;
  errorMessage: string | null;
  allowGeneralKnowledge: boolean;
  /** The main app's UUID for the target. */
  topicId: string | null;
  subChapterId: string | null;
  /** Resolved live from the main app, alongside its sequence number. Null if
   *  the target no longer exists there or the main app was unreachable. */
  targetName: string | null;
  targetSequenceNo: number | null;
  autoApprove?: boolean;
  /** Critique jobs only: the content they reviewed, and the generate job that
   *  produced it. The queue pairs the two into one run. */
  sourceContentId?: number | null;
  reviewsJobId?: number | null;
  knowledgeSourceIds?: number[] | null;
  createdByUserId?: number | null;
  createdAt?: string;
  startedAt?: string | null;
  completedAt?: string | null;
  batchId?: number | null;
  collectionId?: number | null;
  category?: { name: string };
  collection?: { id: number; name: string };
  /** The human reviewer verdict (migration 057). Rows predating it come back
   *  as "PENDING" from the server, never undefined. */
  reviewStatus?: string;
  reviewedByUserName?: string | null;
  reviewedAt?: string | null;
  reviewNote?: string | null;
}

/** GET /generation-jobs/:id/detail — the list row plus everything too large
 *  or too joined to ship on every row. */
interface JobDetail extends JobRow {
  promptSnapshot: string | null;
  sampleSnapshot: string | null;
  knowledgeSources: { id: number; filename: string }[];
  parameters?: {
    model: string;
    isReasoningModel: boolean;
    temperature: number | null;
    topP: number | null;
    frequencyPenalty: number | null;
    presencePenalty: number | null;
    reasoningEffort: string | null;
    seed: number | null;
    maxTokens: number | null;
    systemFingerprint: string | null;
  };
  content: {
    id: number;
    status: string;
    retrievedChunks: number | null;
    ungrounded: boolean;
    notInTextbook: boolean;
    autoApproved: boolean;
    pushStatus: string | null;
    pushError: string | null;
    externalContentId: string | null;
    approvedByUserName?: string | null;
    updatedAt?: string;
    critiqueStatus: string | null;
    critiqueModel: string | null;
    critiqueInputTokens: number | null;
    critiqueOutputTokens: number | null;
    critiqueEstimatedCost: string | number | null;
    critiqueDurationMs: number | null;
    critiquedAt: string | null;
    createdAt: string;
  } | null;
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  const empty = value === null || value === undefined || value === "";
  return (
    <tr>
      <td className="muted mini" style={{ whiteSpace: "nowrap", verticalAlign: "top" }}>
        {label}
      </td>
      <td className="mini">{empty ? <span className="muted">—</span> : value}</td>
    </tr>
  );
}

function sumOrNull(values: (number | null | undefined)[]): number | null {
  const nums = values.filter((v): v is number => v !== null && v !== undefined);
  if (nums.length === 0) return null;
  return nums.reduce((a, b) => a + b, 0);
}

function UsageRow({
  label,
  generation,
  review,
  total,
  emphasis,
}: {
  label: string;
  generation: React.ReactNode;
  review: React.ReactNode;
  total: React.ReactNode;
  emphasis?: boolean;
}) {
  const cls = `mini ${emphasis ? "bold" : ""}`;
  return (
    <tr>
      <td className="muted mini">{label}</td>
      <td className={cls}>{generation ?? <span className="muted">—</span>}</td>
      <td className={cls}>{review ?? <span className="muted">—</span>}</td>
      <td className={cls}>{total ?? <span className="muted">—</span>}</td>
    </tr>
  );
}

function targetLabel(job: JobRow) {
  if (job.targetName) {
    return job.targetSequenceNo !== null
      ? `${job.targetSequenceNo}. ${job.targetName}`
      : job.targetName;
  }
  if (job.topicId) return `Topic ${job.topicId}`;
  if (job.subChapterId) return `Subchapter ${job.subChapterId}`;
  return "Unknown target";
}

interface QueueListResponse {
  rows: JobRow[];
  total: number;
  page: number;
  pageSize: number;
  attentionCount: number;
  facets: {
    collections: { id: number; name: string; count: number }[];
    categories: { id: number; name: string; count: number }[];
    chapters: { id: string; name: string; count: number }[];
  };
}

function JobDetailModal({
  jobId,
  onClose,
  toast,
  onAction,
  isActionPending,
  onViewContent,
  onViewPdf,
  onReviewed,
}: {
  jobId: number;
  onClose: () => void;
  toast: (m: string) => void;
  onAction: (action: "pause" | "resume" | "cancel" | "retry", jobId: number) => Promise<void>;
  /** Mirrors the row's own pending state behind this modal, so its header
   *  buttons and the row's stay in sync rather than tracking their own copy. */
  isActionPending: (action: "pause" | "resume" | "cancel" | "retry", jobId: number) => boolean;
  onViewContent: (data: {
    title: string;
    content: string;
    responseType?: ResponseType;
    critiqueFeedback?: string | null;
    critiqueResponseType?: string | null;
    contentId?: number;
    generatedContent?: string;
    reviewedContent?: string | null;
  }) => void;
  onViewPdf: (data: { id: number; collectionId: number; filename: string }) => void;
  /** Bumps the list's own refresh key so the row behind this modal picks up
   *  the new reviewer badge without waiting for its next poll. */
  onReviewed: () => void;
}) {
  const [detailRefreshKey, setDetailRefreshKey] = useState(0);
  const detail = useApi<JobDetail>(`/generation-jobs/${jobId}/detail`, [jobId, detailRefreshKey]);
  const job = detail.data;
  const content = job?.content ?? null;
  const { isPending: isModalActionPending, run: runModalAction } = usePendingActions();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-card"
        style={{ maxWidth: 900, width: "92%", maxHeight: "90vh", overflowY: "auto" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="row between mb-4" style={{ marginBottom: 16, alignItems: "flex-start", gap: 12 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <h3 style={{ margin: 0 }}>
                {job ? targetLabel(job) : `Job #${jobId}`}
              </h3>
              {job?.status && <StatusBadge status={job.status} />}
              {job?.category && (
                <span className="badge neutral mini" style={{ fontWeight: 600 }}>
                  {job.category.name}
                </span>
              )}
              {job?.model && (
                <span
                  className="badge mini"
                  style={{
                    background: "var(--surface-muted, #f1f5f9)",
                    color: "var(--foreground-muted, #475569)",
                    border: "1px solid var(--border, #cbd5e1)",
                  }}
                >
                  🤖 {job.model}
                </span>
              )}
            </div>
            <div className="mini muted" style={{ marginTop: 4 }}>
              Job #{jobId}
              {job?.collection?.name && ` · ${job.collection.name}`}
              {job?.createdAt && ` · Created ${new Date(job.createdAt).toLocaleString()}`}
            </div>
          </div>

          <div className="row" style={{ gap: 8 }}>
            {job && (
              <>
                {job.status === "PENDING" && (
                  <ActionButton
                    className="mini"
                    pending={isActionPending("pause", job.id)}
                    onClick={() => void onAction("pause", job.id)}
                  >
                    Pause
                  </ActionButton>
                )}
                {job.status === "PAUSED" && (
                  <ActionButton
                    className="mini"
                    pending={isActionPending("resume", job.id)}
                    onClick={() => void onAction("resume", job.id)}
                  >
                    Resume
                  </ActionButton>
                )}
                {["PENDING", "PAUSED", "PROCESSING"].includes(job.status) && (
                  <ActionButton
                    className="mini"
                    pending={isActionPending("cancel", job.id)}
                    onClick={() => void onAction("cancel", job.id)}
                  >
                    Cancel
                  </ActionButton>
                )}
                {["FAILED", "CANCELLED"].includes(job.status) && (
                  <ActionButton
                    className="mini primary"
                    pending={isActionPending("retry", job.id)}
                    onClick={() => void onAction("retry", job.id)}
                  >
                    ↻ Retry
                  </ActionButton>
                )}
              </>
            )}
            <button type="button" className="btn mini" onClick={onClose}>
              ✕ Close
            </button>
          </div>
        </div>

        {detail.loading && !job && <p className="muted">Loading job details…</p>}
        {detail.error && <Notice kind="error">{detail.error}</Notice>}

        {job && (
          <>
            {/* Failure Error Notice */}
            {job.errorMessage && (
              <div
                className="notice error"
                style={{
                  marginBottom: 16,
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 12,
                }}
              >
                <div>
                  <strong>Execution Error:</strong> {job.errorMessage}
                </div>
                {["FAILED", "CANCELLED"].includes(job.status) && (
                  <ActionButton
                    className="mini"
                    pending={isActionPending("retry", job.id)}
                    onClick={() => void onAction("retry", job.id)}
                  >
                    Retry Now
                  </ActionButton>
                )}
              </div>
            )}

            {/* Generated Content Box */}
            {content && (
              <div
                style={{
                  background: "#f8fafc",
                  border: "1px solid var(--border)",
                  borderRadius: 8,
                  padding: "12px 16px",
                  marginBottom: 16,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 10,
                }}
              >
                <div>
                  <strong>Generated Output:</strong>{" "}
                  <span className="badge neutral mini">{content.status}</span>
                  {content.autoApproved && (
                    <span className="badge approved mini" style={{ marginLeft: 6 }}>
                      ⚡ Auto-Approved
                    </span>
                  )}
                  {content.pushStatus && (
                    <span
                      className={`badge mini ${content.pushStatus === "PUSHED" ? "approved" : "failed"}`}
                      style={{ marginLeft: 6 }}
                    >
                      {content.pushStatus === "PUSHED"
                        ? `Pushed #${content.externalContentId ?? ""}`
                        : `Push: ${content.pushStatus}`}
                    </span>
                  )}
                  {content.pushError && (
                    <div className="mini" style={{ color: "var(--red)", marginTop: 4 }}>
                      Push error: {content.pushError}
                    </div>
                  )}
                </div>

                <div className="row" style={{ gap: 8 }}>
                  <ActionButton
                    className="mini"
                    pending={isModalActionPending("view-content")}
                    onClick={() => {
                      void runModalAction("view-content", () =>
                        api.get<any>(`/generated-contents/${content.id}`).then((c) => {
                          onViewContent({
                            title: `Content for Job #${job.id} (${targetLabel(job)})`,
                            // The reviewer's edit, when there is one, is what
                            // actually reflects the reviewed state — the AI's
                            // original (generatedContent) is never overwritten
                            // (see content-service.ts's saveReview).
                            content: c.reviewedContent ?? c.generatedContent,
                            responseType: c.category?.responseType ?? "html",
                            critiqueFeedback: c.critiqueFeedback,
                            critiqueResponseType: c.category?.critiqueResponseType,
                            contentId: c.id,
                            generatedContent: c.generatedContent,
                            reviewedContent: c.reviewedContent,
                          });
                        }),
                      ).catch((err: any) => toast(err.message || "Failed to load content"));
                    }}
                  >
                    👁 View Generated Content
                  </ActionButton>
                  {content.pushStatus !== "PUSHED" && (
                    <ActionButton
                      className="mini primary"
                      pending={isModalActionPending("approve")}
                      onClick={async () => {
                        try {
                          await runModalAction("approve", () =>
                            api.post(`/generated-contents/${content.id}/approve`, {}),
                          );
                          toast("Content approved & pushed to main app!");
                          onClose();
                        } catch (err: any) {
                          toast(err.message || "Failed to approve and push");
                        }
                      }}
                    >
                      ✅ Approve & Push
                    </ActionButton>
                  )}
                </div>
              </div>
            )}

            {job && (
              <ReviewControl
                kind="generation-job"
                exec={job}
                toast={toast}
                onChanged={() => {
                  setDetailRefreshKey((k) => k + 1);
                  onReviewed();
                }}
              />
            )}

            {/* General Info */}
            <div className="book" style={{ padding: 14, borderRadius: 6, marginBottom: 16 }}>
              <h4 style={{ margin: "0 0 8px 0" }}>📋 General Job Information</h4>
              <table style={{ margin: 0, width: "100%" }}>
                <tbody>
                  <Row label="Job ID" value={`#${job.id}`} />
                  <Row label="Job Type" value={job.jobType === "critique" ? "Critique" : "Generate"} />
                  <Row label="Status" value={<StatusBadge status={job.status} />} />
                  <Row label="Target" value={targetLabel(job)} />
                  <Row label="Category" value={job.category?.name} />
                  <Row label="Collection" value={job.collection?.name} />
                  <Row label="Batch" value={job.batchId ? `#${job.batchId}` : "Ungrouped"} />
                  <Row label="Attempts" value={`${job.attemptCount ?? 1} attempt(s)`} />
                  <Row label="Priority" value={job.priority} />
                  <Row label="Auto-approve" value={job.autoApprove ? "Yes" : "No"} />
                  <Row label="Created At" value={job.createdAt ? new Date(job.createdAt).toLocaleString() : "—"} />
                  <Row label="Started At" value={job.startedAt ? new Date(job.startedAt).toLocaleString() : "—"} />
                  <Row label="Completed At" value={job.completedAt ? new Date(job.completedAt).toLocaleString() : "—"} />
                </tbody>
              </table>
            </div>

            {/* Usage Breakdown */}
            <div className="book" style={{ padding: 14, borderRadius: 6, marginBottom: 16 }}>
              <h4 style={{ margin: "0 0 8px 0" }}>📊 Usage & Cost Breakdown</h4>
              <table style={{ background: "#fff", width: "100%", borderRadius: 6 }}>
                <thead>
                  <tr>
                    <th />
                    <th>Generation</th>
                    <th>Review</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  <UsageRow
                    label="Model"
                    generation={job.model}
                    review={content?.critiqueModel ?? null}
                    total={null}
                  />
                  <UsageRow
                    label="Input tokens"
                    generation={compactTokens(job.inputTokens)}
                    review={compactTokens(content?.critiqueInputTokens)}
                    total={compactTokens(sumOrNull([job.inputTokens, content?.critiqueInputTokens]))}
                  />
                  <UsageRow
                    label="Output tokens"
                    generation={compactTokens(job.outputTokens)}
                    review={compactTokens(content?.critiqueOutputTokens)}
                    total={compactTokens(sumOrNull([job.outputTokens, content?.critiqueOutputTokens]))}
                  />
                  <UsageRow
                    label="Cached tokens"
                    generation={
                      job.cachedTokens === null
                        ? null
                        : `${job.cachedTokens.toLocaleString()}${
                            job.inputTokens
                              ? ` (${Math.round((job.cachedTokens / job.inputTokens) * 100)}%)`
                              : ""
                          }`
                    }
                    review={null}
                    total={null}
                  />
                  <UsageRow
                    label="Search calls"
                    generation={job.searchCalls}
                    review={null}
                    total={null}
                  />
                  <UsageRow
                    label="Cost"
                    generation={formatCost(job.estimatedCost)}
                    review={formatCost(content?.critiqueEstimatedCost)}
                    total={formatCost(
                      sumOrNull([
                        job.estimatedCost === null || job.estimatedCost === undefined ? null : Number(job.estimatedCost),
                        content?.critiqueEstimatedCost === null || content?.critiqueEstimatedCost === undefined ? null : Number(content.critiqueEstimatedCost),
                      ]),
                    )}
                    emphasis
                  />
                  <UsageRow
                    label="Duration"
                    generation={formatDuration(job.durationMs)}
                    review={formatDuration(content?.critiqueDurationMs)}
                    total={formatDuration(sumOrNull([job.durationMs, content?.critiqueDurationMs]))}
                  />
                </tbody>
              </table>
            </div>

            {/* Hyperparameters */}
            {job.parameters && (
              <div className="book" style={{ padding: 14, borderRadius: 6, marginBottom: 16 }}>
                <h4 style={{ margin: "0 0 8px 0" }}>⚙️ Model Hyperparameters & Execution Settings</h4>
                <table style={{ margin: 0, width: "100%" }}>
                  <tbody>
                    <Row label="Executed Model" value={job.parameters.model} />
                    <Row
                      label="Model Class"
                      value={
                        job.parameters.isReasoningModel
                          ? "Reasoning / Specialized Model (Sampling Disabled)"
                          : "Standard Sampling Model"
                      }
                    />
                    <Row
                      label="Temperature"
                      value={job.parameters.temperature !== null ? job.parameters.temperature : "N/A"}
                    />
                    <Row
                      label="Top P"
                      value={job.parameters.topP !== null ? job.parameters.topP : "N/A"}
                    />
                    <Row
                      label="Max Tokens"
                      value={job.parameters.maxTokens !== null ? job.parameters.maxTokens : "Default"}
                    />
                    <Row
                      label="Reasoning Effort"
                      value={job.parameters.reasoningEffort ?? "Default"}
                    />
                    <Row
                      label="Seed"
                      value={job.parameters.seed !== null ? job.parameters.seed : "N/A"}
                    />
                  </tbody>
                </table>
              </div>
            )}

            {/* Knowledge Sources */}
            {job.knowledgeSources && job.knowledgeSources.length > 0 && (
              <div className="book" style={{ padding: 14, borderRadius: 6, marginBottom: 16 }}>
                <h4 style={{ margin: "0 0 8px 0" }}>📚 Knowledge Sources Grounding</h4>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {job.knowledgeSources.map((ks) => (
                    <button
                      key={ks.id}
                      type="button"
                      className="btn mini"
                      onClick={() =>
                        onViewPdf({
                          id: ks.id,
                          collectionId: job.collection?.id ?? job.collectionId ?? 0,
                          filename: ks.filename,
                        })
                      }
                    >
                      📄 {ks.filename}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Prompt Instructions Snapshot */}
            {job.promptSnapshot && (
              <div className="book" style={{ padding: 14, borderRadius: 6, marginBottom: 16 }}>
                <div className="row between" style={{ marginBottom: 6 }}>
                  <h4 style={{ margin: 0 }}>📝 System Prompt / Instructions Snapshot</h4>
                  <button
                    type="button"
                    className="btn mini"
                    onClick={() => {
                      void navigator.clipboard.writeText(job.promptSnapshot!);
                      toast("Prompt copied to clipboard!");
                    }}
                  >
                    📋 Copy Prompt
                  </button>
                </div>
                <pre
                  style={{
                    padding: 10,
                    borderRadius: 6,
                    background: "#f8fafc",
                    border: "1px solid var(--border)",
                    fontSize: "0.82rem",
                    maxHeight: 240,
                    overflowY: "auto",
                    whiteSpace: "pre-wrap",
                    margin: 0,
                  }}
                >
                  {job.promptSnapshot}
                </pre>
              </div>
            )}

            {/* Sample Snapshot */}
            {job.sampleSnapshot && (
              <div className="book" style={{ padding: 14, borderRadius: 6, marginBottom: 16 }}>
                <div className="row between" style={{ marginBottom: 6 }}>
                  <h4 style={{ margin: 0 }}>💡 Few-shot / Sample Reference Snapshot</h4>
                  <button
                    type="button"
                    className="btn mini"
                    onClick={() => {
                      void navigator.clipboard.writeText(job.sampleSnapshot!);
                      toast("Sample copied to clipboard!");
                    }}
                  >
                    📋 Copy Sample
                  </button>
                </div>
                <pre
                  style={{
                    padding: 10,
                    borderRadius: 6,
                    background: "#f8fafc",
                    border: "1px solid var(--border)",
                    fontSize: "0.82rem",
                    maxHeight: 200,
                    overflowY: "auto",
                    whiteSpace: "pre-wrap",
                    margin: 0,
                  }}
                >
                  {job.sampleSnapshot}
                </pre>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function GenerationQueueList({ refreshKey, toast }: { refreshKey: number; toast: (m: string) => void }) {
  const [localRefreshKey, setLocalRefreshKey] = useState(0);
  const [detailModalJobId, setDetailModalJobId] = useState<number | null>(null);

  const [filters, setFilters] = useState<QueueFilters>(() =>
    queueInitialFilters(window.location.search),
  );

  useEffect(() => {
    const query = queueSearchFromFilters(filters);
    const next = `${window.location.pathname}${query}`;
    if (next !== `${window.location.pathname}${window.location.search}`) {
      window.history.replaceState(null, "", next);
    }
    queuePersistFilters(filters);
  }, [filters]);

  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filters.search), 300);
    return () => clearTimeout(timer);
  }, [filters.search]);

  const apiQuery = queueApiQueryFromFilters({ ...filters, search: debouncedSearch });
  const queueData = useApi<QueueListResponse>(
    `/generation-jobs/list${apiQuery}`,
    [refreshKey, localRefreshKey, apiQuery],
  );

  const [viewContentModal, setViewContentModal] = useState<{
    title: string;
    content: string;
    responseType?: ResponseType;
    critiqueFeedback?: string | null;
    critiqueResponseType?: string | null;
    /** Set only when this preview is backed by a generated_contents row —
     *  pipeline-execution previews (finalContentText) have no such row, so
     *  editing/review stays unavailable there rather than pointing at a
     *  content id that doesn't exist. */
    contentId?: number;
    generatedContent?: string;
    reviewedContent?: string | null;
  } | null>(null);
  // Same fullscreen mechanics as PipelineExecutionsList's Content modal — a
  // reviewer editing content wants the room fullscreen frees up, especially
  // once the tabbed Content/Mobile Content/Review layout leaves empty space
  // below a fixed-height editor in the default-size modal.
  const [isContentFullScreen, setIsContentFullScreen] = useState(false);

  useEffect(() => {
    if (!viewContentModal) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      if (isContentFullScreen) {
        setIsContentFullScreen(false);
      } else {
        setViewContentModal(null);
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [viewContentModal, isContentFullScreen]);

  const [viewingPdf, setViewingPdf] = useState<{ id: number; collectionId: number; filename: string; roleTag?: string } | null>(null);
  const [viewingDocx, setViewingDocx] = useState<{ filename: string; fileUrl: string; subtitle?: string } | null>(null);
  const [selectedJobIds, setSelectedJobIds] = useState<Set<number>>(new Set());
  const { isPending, run } = usePendingActions();

  useEffect(() => {
    setSelectedJobIds(new Set());
  }, [filters.segment, filters.page, filters.collectionId, filters.categoryId, filters.chapterId]);

  const [now, setNow] = useState<number>(Date.now());

  useEffect(() => {
    const hasProcessing = queueData.data?.rows?.some(
      (job) => job.status === "PROCESSING",
    );
    if (!hasProcessing) return;

    const timer = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(timer);
  }, [queueData.data]);

  async function handleSingleAction(action: "pause" | "resume" | "cancel" | "retry", jobId: number) {
    try {
      await run(`job-${jobId}-${action}`, () => api.post(`/generation-jobs/${jobId}/${action}`, {}));
      toast(`Job #${jobId} ${action === "retry" ? "queued for retry" : action + "d"}`);
      setLocalRefreshKey((k) => k + 1);
    } catch (err: any) {
      toast(err.message || `Failed to ${action} job`);
    }
  }

  async function handleBulkAction(action: "pause" | "resume" | "cancel" | "retry") {
    if (selectedJobIds.size === 0) return;
    try {
      await run("bulk", () =>
        api.post("/generation-jobs/bulk", {
          action,
          ids: Array.from(selectedJobIds),
        }),
      );
      toast(`Action '${action}' applied to ${selectedJobIds.size} job(s)`);
      setSelectedJobIds(new Set());
      setLocalRefreshKey((k) => k + 1);
    } catch (err: any) {
      toast(err.message || `Failed to perform bulk ${action}`);
    }
  }

  if (queueData.loading && !queueData.data)
    return <FullScreenLoader label="Loading generation queue…" />;

  const visible = queueData.data?.rows ?? [];
  const total = queueData.data?.total ?? 0;
  const collectionOptions = queueData.data?.facets?.collections ?? [];
  const categoryOptions = queueData.data?.facets?.categories ?? [];
  const chapterOptions = queueData.data?.facets?.chapters ?? [];
  const attentionCount = queueData.data?.attentionCount ?? 0;

  const pageSize = filters.pageSize;
  const page = filters.page;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, total);

  const allVisibleSelected =
    visible.length > 0 && visible.every((j) => selectedJobIds.has(j.id));
  const someVisibleSelected =
    visible.some((j) => selectedJobIds.has(j.id)) && !allVisibleSelected;

  function toggleSelectAllVisible() {
    if (allVisibleSelected) {
      const next = new Set(selectedJobIds);
      visible.forEach((j) => next.delete(j.id));
      setSelectedJobIds(next);
    } else {
      const next = new Set(selectedJobIds);
      visible.forEach((j) => next.add(j.id));
      setSelectedJobIds(next);
    }
  }

  function toggleSelectOne(id: number) {
    const next = new Set(selectedJobIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedJobIds(next);
  }

  return (
    <>
      {/* Symmetrical Attention Banner */}
      {attentionCount > 0 && filters.segment !== "attention" && (
        <div
          className="exec-attention"
          role="status"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "10px 14px",
            marginBottom: 12,
            borderRadius: 6,
            background: "var(--red-light, #fef2f2)",
            border: "1px solid var(--red-border, #fecaca)",
            color: "var(--red-dark, #991b1b)",
          }}
        >
          <span>
            ⚠️ <strong>{attentionCount}</strong> job{attentionCount === 1 ? " needs" : "s need"} attention (failed, paused, or unpublished)
          </span>
          <button
            type="button"
            className="btn mini"
            onClick={() => setFilters((f) => ({ ...f, segment: "attention", page: 1 }))}
            style={{ fontWeight: 600 }}
          >
            Show attention jobs →
          </button>
        </div>
      )}

      {/* Segments Header */}
      <div className="row between mb-2" style={{ alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div className="exec-segments" role="tablist" aria-label="Filter generation jobs by status">
          {(
            [
              { key: "all", label: "All" },
              {
                key: "attention",
                label: `Needs attention${attentionCount > 0 ? ` (${attentionCount})` : ""}`,
              },
              { key: "running", label: "Running" },
              { key: "completed", label: "Completed" },
              { key: "archived", label: "Archived" },
            ] as const
          ).map(({ key, label }) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={filters.segment === key}
              className={`exec-segment-btn ${filters.segment === key ? "is-active" : ""}`}
              onClick={() => setFilters((f) => ({ ...f, segment: key, page: 1 }))}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Symmetrical Filter Bar */}
      <div className="exec-filters" role="search" aria-label="Filter jobs">
        <input
          type="search"
          className="exec-filter-search"
          placeholder="Search by target, chapter, category, model..."
          value={filters.search}
          onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value, page: 1 }))}
          aria-label="Search jobs"
        />

        <select
          className="exec-filter-select"
          value={filters.range}
          onChange={(e) => setFilters((f) => ({ ...f, range: e.target.value as TimeRange, page: 1 }))}
          aria-label="Time range"
        >
          <option value="today">Today</option>
          <option value="1d">Last 24 hours</option>
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="all">All time</option>
          <option value="custom">Custom range…</option>
        </select>

        {filters.range === "custom" && (
          <>
            <input
              type="date"
              className="exec-filter-select"
              aria-label="Custom range start"
              value={filters.customFrom ?? ""}
              max={filters.customTo ?? undefined}
              onChange={(e) => setFilters((f) => ({ ...f, customFrom: e.target.value || null, page: 1 }))}
            />
            <input
              type="date"
              className="exec-filter-select"
              aria-label="Custom range end"
              value={filters.customTo ?? ""}
              min={filters.customFrom ?? undefined}
              onChange={(e) => setFilters((f) => ({ ...f, customTo: e.target.value || null, page: 1 }))}
            />
          </>
        )}

        <select
          className="exec-filter-select"
          value={filters.collectionId ?? ""}
          onChange={(e) =>
            setFilters((f) => ({
              ...f,
              collectionId: e.target.value === "" ? null : Number(e.target.value),
              page: 1,
            }))
          }
          aria-label="Collection"
        >
          <option value="">All collections</option>
          {collectionOptions.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name} ({c.count})
            </option>
          ))}
        </select>

        <select
          className="exec-filter-select"
          value={filters.categoryId ?? ""}
          onChange={(e) =>
            setFilters((f) => ({
              ...f,
              categoryId: e.target.value === "" ? null : Number(e.target.value),
              page: 1,
            }))
          }
          aria-label="Category"
        >
          <option value="">All categories</option>
          {categoryOptions.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name} ({c.count})
            </option>
          ))}
        </select>

        {chapterOptions.length > 0 && (
          <select
            className="exec-filter-select"
            value={filters.chapterId ?? ""}
            onChange={(e) =>
              setFilters((f) => ({
                ...f,
                chapterId: e.target.value || null,
                page: 1,
              }))
            }
            aria-label="Chapter"
            title="Filter by the target's chapter"
          >
            <option value="">All chapters</option>
            {chapterOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.count})
              </option>
            ))}
          </select>
        )}

        <select
          className="exec-filter-select"
          aria-label="Reviewer status"
          title="Filter by the human reviewer's verdict"
          value={filters.reviewStatus ?? ""}
          onChange={(e) =>
            setFilters((f) => ({
              ...f,
              reviewStatus: isReviewStatus(e.target.value) ? e.target.value : null,
              page: 1,
            }))
          }
        >
          <option value="">Any review status</option>
          {ALL_REVIEW_STATUSES.map((st) => (
            <option key={st} value={st}>
              {REVIEW_STATUS_META[st].label}
            </option>
          ))}
        </select>

        <select
          className="exec-filter-select"
          aria-label="Approval status"
          title="Filter by whether the content was actually approved and pushed"
          value={filters.approvalStatus ?? ""}
          onChange={(e) =>
            setFilters((f) => ({
              ...f,
              approvalStatus:
                e.target.value === "approved" || e.target.value === "not_approved" ? e.target.value : null,
              page: 1,
            }))
          }
        >
          <option value="">Any approval status</option>
          <option value="approved">Approved</option>
          <option value="not_approved">Not approved</option>
        </select>

        <select
          className="exec-filter-select"
          value={`${filters.sort}:${filters.direction}`}
          onChange={(e) => {
            const [sort, direction] = e.target.value.split(":");
            setFilters((f) => ({
              ...f,
              sort: sort as ExecutionFilters["sort"],
              direction: direction as ExecutionFilters["direction"],
              page: 1,
            }));
          }}
          aria-label="Sort order"
        >
          <option value="createdAt:desc">Newest first</option>
          <option value="createdAt:asc">Oldest first</option>
          <option value="completedAt:desc">Recently finished</option>
          <option value="duration:desc">Slowest first</option>
          <option value="cost:desc">Most expensive</option>
          <option value="status:asc">Status</option>
        </select>

        {(filters.search ||
          filters.collectionId !== null ||
          filters.categoryId !== null ||
          filters.chapterId !== null ||
          filters.range !== "all" ||
          filters.sort !== "createdAt") && (
          <button
            type="button"
            className="exec-filter-clear"
            onClick={() =>
              setFilters((f) => ({
                ...f,
                search: "",
                collectionId: null,
                categoryId: null,
                chapterId: null,
                range: "all",
                customFrom: null,
                customTo: null,
                sort: "createdAt",
                direction: "desc",
                page: 1,
              }))
            }
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Bulk Action Controls Bar */}
      {selectedJobIds.size > 0 && (
        <div
          className="row between"
          style={{
            background: "#eff6ff",
            border: "1px solid #bfdbfe",
            padding: "8px 14px",
            borderRadius: 6,
            marginBottom: 12,
            alignItems: "center",
          }}
        >
          <span style={{ fontSize: "0.85rem", color: "#1e40af", fontWeight: 600 }}>
            {selectedJobIds.size} job{selectedJobIds.size === 1 ? "" : "s"} selected
          </span>
          <div className="row" style={{ gap: 8 }}>
            <ActionButton className="mini" pending={isPending("bulk")} onClick={() => void handleBulkAction("pause")}>
              ⏸ Pause Selected
            </ActionButton>
            <ActionButton className="mini" pending={isPending("bulk")} onClick={() => void handleBulkAction("resume")}>
              ▶ Resume Selected
            </ActionButton>
            <ActionButton className="mini" pending={isPending("bulk")} onClick={() => void handleBulkAction("cancel")}>
              ⏹ Cancel Selected
            </ActionButton>
            <ActionButton className="mini primary" pending={isPending("bulk")} onClick={() => void handleBulkAction("retry")}>
              ↻ Retry Selected
            </ActionButton>
          </div>
        </div>
      )}

      {/* Job Rows */}
      <RefetchOverlay loading={queueData.loading && queueData.data !== null}>
      {visible.length === 0 ? (
        <div className="book" style={{ padding: 24, textAlign: "center" }}>
          <p className="muted" style={{ margin: 0 }}>
            No generation jobs match the selected filters.
          </p>
        </div>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 8px", marginBottom: 6 }}>
            <input
              type="checkbox"
              checked={allVisibleSelected}
              ref={(el) => {
                if (el) el.indeterminate = someVisibleSelected;
              }}
              onChange={toggleSelectAllVisible}
              aria-label="Select all visible jobs"
            />
            <span className="mini muted">Select all visible ({visible.length})</span>
          </div>

          {visible.map((job) => {
            const isRowSelected = selectedJobIds.has(job.id);
            const liveDuration =
              job.status === "PROCESSING" && job.startedAt
                ? Math.max(0, now - new Date(job.startedAt).getTime())
                : job.durationMs;

            return (
              <div
                key={job.id}
                className={`book exec-row is-${severityOf(job)}`}
                style={{
                  marginBottom: 10,
                  transition: "all 0.15s ease",
                  borderLeft: isRowSelected ? "4px solid var(--primary, #2563eb)" : undefined,
                  cursor: "pointer",
                }}
                onClick={() => setDetailModalJobId(job.id)}
              >
                {/* Header Summary Row */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px 14px",
                    gap: 12,
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 280 }}>
                    <input
                      type="checkbox"
                      checked={isRowSelected}
                      onChange={() => toggleSelectOne(job.id)}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Select job #${job.id}`}
                    />

                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        {job.category && (
                          <span
                            className="badge neutral mini"
                            style={{ fontWeight: 600 }}
                          >
                            {job.category.name}
                          </span>
                        )}
                        {job.model && (
                          <span
                            className="badge mini"
                            style={{
                              background: "var(--surface-muted, #f1f5f9)",
                              color: "var(--foreground-muted, #475569)",
                              border: "1px solid var(--border, #cbd5e1)",
                            }}
                          >
                            🤖 {job.model}
                          </span>
                        )}
                        <strong style={{ fontSize: "0.95rem" }}>
                          {targetLabel(job)}
                        </strong>
                        <StatusBadge status={job.status} />
                        {(() => {
                          const jobReviewStatus: ReviewStatus = isReviewStatus(job.reviewStatus ?? "")
                            ? (job.reviewStatus as ReviewStatus)
                            : "PENDING";
                          // PENDING is the common case and would be noise on
                          // every row — same reasoning as the pipeline
                          // execution row's badge.
                          if (jobReviewStatus === "PENDING") return null;
                          return (
                            <span
                              className={`badge ${REVIEW_STATUS_META[jobReviewStatus].badge} mini`}
                              title={`${REVIEW_STATUS_META[jobReviewStatus].hint}${
                                job.reviewedByUserName ? ` — ${job.reviewedByUserName}` : ""
                              }`}
                            >
                              👤 {REVIEW_STATUS_META[jobReviewStatus].label}
                            </span>
                          );
                        })()}
                        {job.autoApprove && (
                          <span className="badge neutral mini">Auto-approve</span>
                        )}
                        {job.collection && (
                          <span className="mini muted">· {job.collection.name}</span>
                        )}
                      </div>

                      <div className="mini muted" style={{ marginTop: 2 }}>
                        Job #{job.id} · Created {job.createdAt ? new Date(job.createdAt).toLocaleString() : "—"}
                        {job.attemptCount > 1 && ` · Attempt ${job.attemptCount}`}
                        {job.priority !== 0 && ` · Priority ${job.priority}`}
                      </div>
                    </div>
                  </div>

                  {/* Usage Metrics */}
                  <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                    <div className="mini muted" style={{ textAlign: "right" }}>
                      <div>
                        {job.inputTokens !== null || job.outputTokens !== null ? (
                          <>
                            {compactTokens(job.inputTokens)} in / {compactTokens(job.outputTokens)} out
                          </>
                        ) : (
                          "—"
                        )}
                      </div>
                      <div style={{ fontWeight: 600, color: "var(--foreground)" }}>
                        {compactCost(job.estimatedCost)}
                        {liveDuration !== null && liveDuration !== undefined && (
                          <span style={{ marginLeft: 6, fontWeight: "normal", color: "var(--foreground-muted)" }}>
                            ⏱️ {formatDuration(liveDuration)}{job.status === "PROCESSING" ? " (live)" : ""}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div className="row" style={{ gap: 6 }} onClick={(e) => e.stopPropagation()}>
                      {job.status === "PENDING" && (
                        <ActionButton
                          className="mini"
                          pending={isPending(`job-${job.id}-pause`)}
                          onClick={() => void handleSingleAction("pause", job.id)}
                        >
                          Pause
                        </ActionButton>
                      )}
                      {job.status === "PAUSED" && (
                        <ActionButton
                          className="mini"
                          pending={isPending(`job-${job.id}-resume`)}
                          onClick={() => void handleSingleAction("resume", job.id)}
                        >
                          Resume
                        </ActionButton>
                      )}
                      {["PENDING", "PAUSED", "PROCESSING"].includes(job.status) && (
                        <ActionButton
                          className="mini"
                          pending={isPending(`job-${job.id}-cancel`)}
                          onClick={() => void handleSingleAction("cancel", job.id)}
                        >
                          Cancel
                        </ActionButton>
                      )}
                      {["FAILED", "CANCELLED"].includes(job.status) && (
                        <ActionButton
                          className="mini"
                          pending={isPending(`job-${job.id}-retry`)}
                          onClick={() => void handleSingleAction("retry", job.id)}
                        >
                          ↻ Retry
                        </ActionButton>
                      )}
                      <button
                        type="button"
                        className="btn mini"
                        onClick={() => setDetailModalJobId(job.id)}
                      >
                        👁 Details
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* Symmetrical Pager Footer */}
      <div className="exec-pager" role="navigation" aria-label="Pagination">
        <label className="mini muted" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          Page size:
          <select
            className="input mini"
            value={pageSize}
            onChange={(e) =>
              setFilters((f) => ({
                ...f,
                pageSize: Number(e.target.value),
                page: 1,
              }))
            }
          >
            {PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>

        <span className="mini muted">
          {rangeStart}–{rangeEnd} of {total}
        </span>

        <div className="row" style={{ gap: 4 }}>
          <button
            type="button"
            className="btn mini"
            disabled={page <= 1}
            onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))}
            aria-label="Previous page"
          >
            ‹ Prev
          </button>
          <span className="mini" style={{ padding: "0 6px", lineHeight: "24px" }}>
            {page} / {totalPages}
          </span>
          <button
            type="button"
            className="btn mini"
            disabled={page >= totalPages}
            onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}
            aria-label="Next page"
          >
            Next ›
          </button>
        </div>
      </div>
      </RefetchOverlay>

      {/* Shared Modals */}
      {viewContentModal && (
        <div
          className={`modal-backdrop sourced-content-backdrop ${isContentFullScreen ? "viewer-fullscreen-backdrop" : ""}`}
          onClick={() => {
            setViewContentModal(null);
            setIsContentFullScreen(false);
          }}
        >
          <div
            className={`modal-card sourced-content-card ${isContentFullScreen ? "viewer-fullscreen-card" : ""}`}
            style={{ maxWidth: isContentFullScreen ? undefined : 880, width: isContentFullScreen ? undefined : "90%" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="row between mb-4" style={{ marginBottom: 14, flex: "0 0 auto" }}>
              <h3 style={{ margin: 0 }}>{viewContentModal.title}</h3>
              <div className="row" style={{ gap: 8 }}>
                <button
                  type="button"
                  className="btn mini"
                  onClick={() => {
                    void navigator.clipboard.writeText(viewContentModal.content);
                    toast("Content copied to clipboard!");
                  }}
                >
                  📋 Copy
                </button>
                <button
                  type="button"
                  className="btn mini"
                  title={isContentFullScreen ? "Exit Fullscreen" : "Fullscreen — more room to review and edit"}
                  onClick={() => setIsContentFullScreen((f) => !f)}
                >
                  {isContentFullScreen ? "⤡ Exit Fullscreen" : "⛶ Fullscreen"}
                </button>
                <button
                  type="button"
                  className="btn mini"
                  onClick={() => {
                    setViewContentModal(null);
                    setIsContentFullScreen(false);
                  }}
                >
                  ✕ Close
                </button>
              </div>
            </div>

            <div style={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto" }}>

            {viewContentModal.contentId ? (
              <ContentReviewSection
                contentId={viewContentModal.contentId}
                generatedContent={viewContentModal.generatedContent ?? viewContentModal.content}
                reviewedContent={viewContentModal.reviewedContent ?? null}
                responseType={viewContentModal.responseType}
                toast={toast}
                onSaved={(reviewedContent) => {
                  setViewContentModal((m) =>
                    m ? { ...m, reviewedContent, content: reviewedContent ?? m.generatedContent ?? m.content } : m,
                  );
                }}
              />
            ) : (
              <ContentPreview
                content={viewContentModal.content}
                responseType={viewContentModal.responseType ?? "html"}
              />
            )}

            {viewContentModal.critiqueFeedback && (
              <div style={{ marginTop: 20, paddingTop: 14, borderTop: "1px solid var(--border)" }}>
                <div className="row between" style={{ marginBottom: 8 }}>
                  <h4 style={{ margin: 0 }}>Review / Critique Feedback</h4>
                  <button
                    type="button"
                    className="btn mini"
                    onClick={() => {
                      void navigator.clipboard.writeText(viewContentModal.critiqueFeedback!);
                      toast("Critique copied to clipboard!");
                    }}
                  >
                    📋 Copy Critique
                  </button>
                </div>
                <ContentPreview
                  content={viewContentModal.critiqueFeedback}
                  responseType={viewContentModal.critiqueResponseType === "json" ? "json" : "markdown"}
                />
              </div>
            )}
            </div>
          </div>
        </div>
      )}

      {detailModalJobId !== null && (
        <JobDetailModal
          jobId={detailModalJobId}
          onClose={() => setDetailModalJobId(null)}
          toast={toast}
          onAction={handleSingleAction}
          isActionPending={(action, jobId) => isPending(`job-${jobId}-${action}`)}
          onViewContent={(data) => setViewContentModal(data)}
          onViewPdf={(data) => setViewingPdf(data)}
          onReviewed={() => setLocalRefreshKey((k) => k + 1)}
        />
      )}

      {viewingPdf && (
        <PdfViewerModal
          source={viewingPdf}
          onClose={() => setViewingPdf(null)}
        />
      )}

      {viewingDocx && (
        <DocxViewerModal
          filename={viewingDocx.filename}
          fileUrl={viewingDocx.fileUrl}
          subtitle={viewingDocx.subtitle}
          onClose={() => setViewingDocx(null)}
        />
      )}
    </>
  );
}

function ManualStepCorrectionModal({
  execId,
  stepExec,
  onClose,
  onSubmitted,
  toast,
}: {
  execId: number;
  stepExec: any;
  onClose: () => void;
  onSubmitted: () => void;
  toast: (m: string) => void;
}) {
  const existingInputs = stepExec.resolvedInputs ?? {};
  const [manualInputs, setManualInputs] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    if (stepExec.manualInputs) {
      for (const [k, v] of Object.entries(stepExec.manualInputs)) {
        init[k] = String(v ?? "");
      }
    } else {
      for (const [k, v] of Object.entries(existingInputs)) {
        init[k] = String(v ?? "");
      }
    }
    return init;
  });

  const [promptOverride, setPromptOverride] = useState<string>(
    stepExec.manualPromptOverride ?? stepExec.stepSnapshot?.prompt ?? "",
  );
  // Per attempt, so both start empty rather than echoing the previous rerun.
  const [additionalInstructions, setAdditionalInstructions] = useState<string>("");
  const [excludedInputs, setExcludedInputs] = useState<string[]>([]);
  const [excludePromptDoc, setExcludePromptDoc] = useState(false);
  const promptDocName: string | null = stepExec.stepSnapshot?.promptDocFileId
    ? (stepExec.stepSnapshot.promptDocFilename ?? "attached document")
    : null;
  // Mapped inputs with no value shown above (a step that has never run, or
  // whose values were not recorded) still need an include/exclude control.
  const mappedOnlyInputs: string[] = (
    (stepExec.stepSnapshot?.inputMappings ?? []) as { varName: string }[]
  )
    .map((m) => m.varName)
    .filter((name, i, all) => !(name in manualInputs) && all.indexOf(name) === i);
  const toggleInput = (name: string, include: boolean) =>
    setExcludedInputs((prev) => (include ? prev.filter((n) => n !== name) : [...prev, name]));
  const [correctionNotes, setCorrectionNotes] = useState<string>(stepExec.correctionNotes ?? "");
  const [resetDownstream, setResetDownstream] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const extraParamsObj = stepExec.manualExtraParams ?? stepExec.extraParameters ?? stepExec.stepSnapshot?.extraParameters ?? {};
  const [temp, setTemp] = useState<string>(extraParamsObj.temperature !== undefined && extraParamsObj.temperature !== null ? String(extraParamsObj.temperature) : "");
  const [topP, setTopP] = useState<string>(extraParamsObj.topP !== undefined && extraParamsObj.topP !== null ? String(extraParamsObj.topP) : "");
  const [maxTokens, setMaxTokens] = useState<string>(extraParamsObj.maxTokens !== undefined && extraParamsObj.maxTokens !== null ? String(extraParamsObj.maxTokens) : "");
  const [reasoningEffort, setReasoningEffort] = useState<string>(extraParamsObj.reasoningEffort ? String(extraParamsObj.reasoningEffort) : "");

  async function handleRerun() {
    setSubmitting(true);
    setError(null);
    try {
      const manualExtraParams: Record<string, unknown> = {};
      if (temp !== "") manualExtraParams.temperature = parseFloat(temp);
      if (topP !== "") manualExtraParams.topP = parseFloat(topP);
      if (maxTokens !== "") manualExtraParams.maxTokens = parseInt(maxTokens, 10);
      if (reasoningEffort !== "") manualExtraParams.reasoningEffort = reasoningEffort;

      await api.post(`/pipelines/executions/${execId}/rerun-step/${stepExec.id}`, {
        manualInputs: Object.fromEntries(
          Object.entries(manualInputs).filter(([name]) => !excludedInputs.includes(name)),
        ),
        manualExcludedInputs: excludedInputs.length > 0 ? excludedInputs : undefined,
        manualExcludePromptDoc: excludePromptDoc || undefined,
        manualAdditionalInstructions: additionalInstructions.trim() ? additionalInstructions : undefined,
        manualPromptOverride: promptOverride.trim() !== (stepExec.stepSnapshot?.prompt ?? "").trim() ? promptOverride : undefined,
        manualExtraParams: Object.keys(manualExtraParams).length > 0 ? manualExtraParams : undefined,
        correctionNotes: correctionNotes.trim() ? correctionNotes : undefined,
        resetDownstream,
      });

      toast(`Step ${stepExec.stepOrder} (${stepExec.stepName}) queued for rerun with manual corrections`);
      onSubmitted();
      onClose();
    } catch (err: any) {
      setError(err.message || "Failed to rerun step");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-card" style={{ maxWidth: 840, width: "90%", maxHeight: "90vh", overflowY: "auto" }} onClick={(e) => e.stopPropagation()}>
        <div className="row between mb-4" style={{ marginBottom: 16 }}>
          <h3 style={{ margin: 0 }}>
            ✏️ Re-run Step #{stepExec.stepOrder}: {stepExec.stepName} with Manual Corrections
          </h3>
          <button type="button" className="btn mini" onClick={onClose}>
            ✕ Close
          </button>
        </div>

        {error && <Notice kind="error">{error}</Notice>}

        <div className="notice info" style={{ marginBottom: 16 }}>
          Edit manual input variable corrections, system prompt instructions, or model parameters below.
          {resetDownstream && " Re-running will also reset subsequent steps in this execution to PENDING."}
        </div>

        {/* Input variables section */}
        <h4 style={{ margin: "12px 0 6px 0" }}>Input Variable Corrections</h4>
        {Object.keys(manualInputs).length === 0 && mappedOnlyInputs.length === 0 ? (
          <p className="muted mini">No input variables are mapped for this step.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
            {Object.entries(manualInputs).map(([varName, val]) => {
              const included = !excludedInputs.includes(varName);
              return (
                <div key={varName} style={{ opacity: included ? 1 : 0.5 }}>
                  <label className="row mini" style={{ fontWeight: 600, marginBottom: 4, cursor: "pointer" }}>
                    <input type="checkbox" checked={included} onChange={(e) => toggleInput(varName, e.target.checked)} />
                    <span>Include input <code>[{varName}]</code></span>
                  </label>
                  <textarea
                    className="input"
                    style={{ width: "100%", height: 70, fontSize: "0.85rem", fontFamily: "monospace" }}
                    value={val}
                    disabled={!included}
                    onChange={(e) => setManualInputs({ ...manualInputs, [varName]: e.target.value })}
                  />
                </div>
              );
            })}
            {mappedOnlyInputs.map((varName) => (
              <label key={varName} className="row mini" style={{ fontWeight: 600, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={!excludedInputs.includes(varName)}
                  onChange={(e) => toggleInput(varName, e.target.checked)}
                />
                <span>Include input <code>[{varName}]</code> <span className="muted">(resolved from its mapping at run time)</span></span>
              </label>
            ))}
            <p className="muted mini" style={{ margin: 0 }}>
              Untick an input to leave it out of this rerun only.
            </p>
          </div>
        )}

        {promptDocName && (
          <>
            <h4 style={{ margin: "12px 0 6px 0" }}>Attached Prompt Document</h4>
            <label className="row mini" style={{ cursor: "pointer", marginBottom: 4 }}>
              <input type="checkbox" checked={!excludePromptDoc} onChange={(e) => setExcludePromptDoc(!e.target.checked)} />
              <span>Include <code>{promptDocName}</code> in this rerun</span>
            </label>
            <p className="muted mini" style={{ margin: "0 0 16px 0" }}>
              {excludePromptDoc
                ? "The document is left out of this rerun. Add additional instructions or an override below if the step has no text prompt."
                : "Sent alongside the instructions below. Unticking affects this rerun only."}
            </p>
          </>
        )}

        {/* Additional instructions: appended, never replacing */}
        <h4 style={{ margin: "12px 0 6px 0" }}>Additional Instructions (this rerun only)</h4>
        <textarea
          className="input"
          style={{ width: "100%", height: 90, fontSize: "0.85rem", fontFamily: "monospace", marginBottom: 4 }}
          value={additionalInstructions}
          onChange={(e) => setAdditionalInstructions(e.target.value)}
          placeholder="Added after the step's existing instructions (including any attached document). Not saved to the step."
        />
        <p className="muted mini" style={{ margin: "0 0 16px 0" }}>
          Use this to add to the step's instructions. To replace the text prompt instead, edit the override below.
        </p>

        {/* Prompt override section */}
        <h4 style={{ margin: "12px 0 6px 0" }}>Prompt Instructions Override</h4>
        <textarea
          className="input"
          style={{ width: "100%", height: 120, fontSize: "0.85rem", fontFamily: "monospace", marginBottom: 16 }}
          value={promptOverride}
          onChange={(e) => setPromptOverride(e.target.value)}
          placeholder="System prompt instructions override for this step run..."
        />

        {/* Extra LLM Parameters Section */}
        <h4 style={{ margin: "12px 0 6px 0" }}>Extra Model Parameters (Step Override)</h4>
        <div className="row" style={{ gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
          <div>
            <label className="mini muted" style={{ display: "block", marginBottom: 2 }}>Temperature (0.0 - 2.0)</label>
            <input type="number" step="0.1" min="0" max="2" className="input mini" style={{ width: 100 }} value={temp} onChange={(e) => setTemp(e.target.value)} placeholder="0.7" />
          </div>
          <div>
            <label className="mini muted" style={{ display: "block", marginBottom: 2 }}>Top P (0.0 - 1.0)</label>
            <input type="number" step="0.05" min="0" max="1" className="input mini" style={{ width: 100 }} value={topP} onChange={(e) => setTopP(e.target.value)} placeholder="1.0" />
          </div>
          <div>
            <label className="mini muted" style={{ display: "block", marginBottom: 2 }}>Max Tokens</label>
            <input type="number" className="input mini" style={{ width: 110 }} value={maxTokens} onChange={(e) => setMaxTokens(e.target.value)} placeholder="Default" />
          </div>
          <div>
            <label className="mini muted" style={{ display: "block", marginBottom: 2 }}>Reasoning Effort</label>
            <select className="input mini" style={{ width: 110 }} value={reasoningEffort} onChange={(e) => setReasoningEffort(e.target.value)}>
              <option value="">Default</option>
              <option value="low">low</option>
              <option value="medium">medium</option>
              <option value="high">high</option>
            </select>
          </div>
        </div>

        {/* Correction notes */}
        <h4 style={{ margin: "12px 0 6px 0" }}>Correction Reason / Notes</h4>
        <input
          type="text"
          className="input"
          style={{ width: "100%", marginBottom: 16 }}
          placeholder="e.g., Corrected input formula context from step 1 output"
          value={correctionNotes}
          onChange={(e) => setCorrectionNotes(e.target.value)}
        />

        <div style={{ marginBottom: 16 }}>
          <label className="row mini" style={{ cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={resetDownstream}
              onChange={(e) => setResetDownstream(e.target.checked)}
            />
            <span><b>Reset downstream steps:</b> Re-run will reset Step #{stepExec.stepOrder + 1} onwards to PENDING so they update with the corrected output.</span>
          </label>
        </div>

        <div className="row" style={{ gap: 8, justifyContent: "flex-end" }}>
          <button type="button" className="btn mini" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <ActionButton className="mini primary" pending={submitting} onClick={() => void handleRerun()}>
            🚀 Re-run Step with Corrections
          </ActionButton>
        </div>
      </div>
    </div>
  );
}

/**
 * An "i" button that reveals secondary detail on demand.
 *
 * The expanded execution view previously laid every figure out inline, which
 * put reconciliation data (summed tokens, cost, created-at) at the same visual
 * weight as the thing an operator opened the row to see — which step broke.
 * Anything a person consults occasionally rather than scans belongs here.
 */
function InfoPopover({ label, children }: { label: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <span style={{ position: "relative", display: "inline-flex" }} className="info-wrap">
      <button
        type="button"
        className="info-btn"
        aria-label={label}
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        i
      </button>
      {open && (
        <>
          <span
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
            style={{ position: "fixed", inset: 0, zIndex: 30 }}
          />
          <div className="info-pop" role="dialog" aria-label={label} onClick={(e) => e.stopPropagation()}>
            {children}
          </div>
        </>
      )}
    </span>
  );
}

/** The paginated shape the executions endpoint returns. Rows carry a
 *  `stepSummary` in place of the full step graph, which is fetched only when a
 *  row is expanded. */
interface ExecutionListResponse {
  rows: any[];
  total: number;
  page: number;
  pageSize: number;
  attentionCount: number;
  facets: {
    collections: { id: number; name: string; count: number }[];
    pipelines: { id: number; name: string; count: number }[];
    chapters: { id: string; name: string; count: number }[];
  };
}

function PipelineExecutionsList({ refreshKey, toast }: { refreshKey: number; toast: (m: string) => void }) {
  const [localRefreshKey, setLocalRefreshKey] = useState(0);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // Filters live in the URL so an operator who finds a broken run can paste
  // the link to whoever owns it, and are mirrored to localStorage so coming
  // back to the screen resumes where you left off. The URL wins on load:
  // otherwise a shared link would open on the recipient's own filters.
  const [filters, setFilters] = useState<ExecutionFilters>(() =>
    initialFilters(window.location.search),
  );

  useEffect(() => {
    const query = searchFromFilters(filters);
    const next = `${window.location.pathname}${query}`;
    if (next !== `${window.location.pathname}${window.location.search}`) {
      window.history.replaceState(null, "", next);
    }
    persistFilters(filters);
  }, [filters]);

  // Typing is debounced before it becomes a request: the search box fires on
  // every keystroke, and each one is now a database query rather than an
  // in-memory filter over rows already loaded.
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filters.search), 300);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // The time range resolves against "now" at request time, not the ticking
  // clock used for live durations — including `now` here would refetch the
  // list five times a second while anything is running.
  const apiQuery = apiQueryFromFilters({ ...filters, search: debouncedSearch });

  // Auto-refresh while anything is in flight, so a running pipeline's status
  // updates without a manual click. This alone used to blur the table on
  // every tick — useApi flipped `loading` true for every refetch, background
  // poll or not, and RefetchOverlay dimmed accordingly. `isPollTick` tells
  // useApi which kind of refetch this is: a poll keeps the old rows on screen
  // with no loading flip at all, while a real change (filters, actions,
  // manual refresh) still shows the overlay if it's slow.
  const [pollKey, setPollKey] = useState(0);
  // Tracks which deps last changed. Read during render (not from an effect)
  // so the value useApi sees for a given render matches the trigger that
  // produced it: a poll bump is background, anything else — filters, an
  // action, the manual Refresh button — gets the normal loading treatment.
  const pollOnlyChange = useRef(false);
  const previousNonPollDeps = useRef([refreshKey, localRefreshKey, apiQuery]);
  const nonPollDeps = [refreshKey, localRefreshKey, apiQuery];
  const nonPollDepsChanged = nonPollDeps.some((v, i) => v !== previousNonPollDeps.current[i]);
  if (nonPollDepsChanged) {
    previousNonPollDeps.current = nonPollDeps;
    pollOnlyChange.current = false;
  } else if (pollKey > 0) {
    pollOnlyChange.current = true;
  }

  const executions = useApi<ExecutionListResponse>(
    `/pipelines/executions/list${apiQuery}`,
    [refreshKey, localRefreshKey, apiQuery, pollKey],
    { background: pollOnlyChange.current },
  );

  useEffect(() => {
    const hasActive = executions.data?.rows?.some((exec: any) =>
      ["PENDING", "PROCESSING", "PAUSED"].includes(exec.status),
    );
    if (!hasActive) return;
    const timer = setInterval(() => setPollKey((k) => k + 1), 4000);
    return () => clearInterval(timer);
  }, [executions.data]);

  // The step graph for whichever rows have been expanded. The list endpoint
  // returns only a summary per row — the graph runs to tens of steps and their
  // retry logs, which is what made the full list slow to load — so the detail
  // endpoint supplies the rest on demand.
  const [stepDetail, setStepDetail] = useState<Record<number, any>>({});
  const [detailLoading, setDetailLoading] = useState<number | null>(null);

  useEffect(() => {
    if (expandedId === null) return;
    let cancelled = false;
    setDetailLoading(expandedId);

    api
      .get<any>(`/pipelines/executions/${expandedId}`)
      .then((full) => {
        if (!cancelled) setStepDetail((current) => ({ ...current, [expandedId]: full }));
      })
      .catch(() => {
        // Leave the row expanded on its summary rather than collapsing it: the
        // header already carries the status and error, which is most of what
        // an operator opened it for.
      })
      .finally(() => {
        if (!cancelled) setDetailLoading((id) => (id === expandedId ? null : id));
      });

    return () => {
      cancelled = true;
    };
    // Refetched on refresh too, so an expanded row follows a running pipeline.
  }, [expandedId, refreshKey, localRefreshKey]);

  const [viewContentModal, setViewContentModal] = useState<{
    title: string;
    content: string;
    responseType?: ResponseType;
    collectionId: number | null;
    sources: ContentSource[];
    /** Set only when this execution has a linked generated_contents row —
     *  see SourcedContentView's `review` prop. */
    contentId?: number;
    generatedContent?: string;
    reviewedContent?: string | null;
  } | null>(null);
  // Fullscreen for the Content modal specifically: a reviewer comparing a PDF
  // page against generated text wants all the room the screen has, the same
  // reasoning PdfViewerModal already applies to a single PDF pane. Opens
  // fullscreen by default — the two setViewContentModal call sites below
  // also set this back to true on each open, so exiting fullscreen for one
  // run doesn't carry over and leave the next one small.
  const [isContentFullScreen, setIsContentFullScreen] = useState(true);

  useEffect(() => {
    if (!viewContentModal) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      if (isContentFullScreen) {
        setIsContentFullScreen(false);
      } else {
        setViewContentModal(null);
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [viewContentModal, isContentFullScreen]);

  const [inspectStep, setInspectStep] = useState<any | null>(null);
  const [rerunModalStep, setRerunModalStep] = useState<{ execId: number; stepExec: any } | null>(null);
  const [viewingDocx, setViewingDocx] = useState<{ filename: string; fileUrl: string; subtitle?: string } | null>(null);
  const [viewingPdf, setViewingPdf] = useState<{ id: number; collectionId: number; filename: string; roleTag?: string } | null>(null);
  const [selectedExecIds, setSelectedExecIds] = useState<Set<number>>(new Set());
  const { isPending, run } = usePendingActions();

  // Clear selections when filters or page change
  useEffect(() => {
    setSelectedExecIds(new Set());
  }, [filters.segment, filters.page, filters.collectionId, filters.pipelineId, filters.chapterId]);

  const isArchivable = (status: string) =>
    ["COMPLETED", "APPROVED", "FAILED", "CANCELLED"].includes(status);

  const [now, setNow] = useState<number>(Date.now());

  // Live timer tick for PROCESSING step executions
  useEffect(() => {
    const hasProcessing = executions.data?.rows?.some(
      (exec: any) =>
        exec.status === "PROCESSING" ||
        exec.stepExecutions?.some((se: any) => se.status === "PROCESSING"),
    );
    if (!hasProcessing) return;

    const timer = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(timer);
  }, [executions.data]);

  async function retryStep(execId: number, stepExecId: number) {
    try {
      await run(`retry-step-${stepExecId}`, () =>
        api.post(`/pipelines/executions/${execId}/retry-step/${stepExecId}`, {}),
      );
      toast("Step reset to PENDING for retry");
      setLocalRefreshKey((k) => k + 1);
    } catch (err: any) {
      toast(err.message || "Failed to retry step");
    }
  }

  if (executions.loading && !executions.data)
    return <FullScreenLoader label="Loading pipeline executions…" />;

  const visible = executions.data?.rows ?? [];
  const total = executions.data?.total ?? 0;
  const collectionOptions = executions.data?.facets?.collections ?? [];
  const pipelineOptions = executions.data?.facets?.pipelines ?? [];
  const chapterOptions = executions.data?.facets?.chapters ?? [];
  const pageCount = Math.max(1, Math.ceil(total / (executions.data?.pageSize || DEFAULT_PAGE_SIZE)));
  const attentionCount = executions.data?.attentionCount ?? 0;

  // An empty first page under no filters means there is genuinely nothing yet,
  // which needs different wording from "nothing matches what you picked".
  const hasNoRunsAtAll =
    total === 0 &&
    filters.segment === "all" &&
    !filters.search.trim() &&
    filters.collectionId === null &&
    filters.pipelineId === null &&
    filters.chapterId === null &&
    filters.range === "all";

  if (hasNoRunsAtAll) {
    return <p className="muted">No pipeline executions found. Trigger one from Content Generation.</p>;
  }

  const segments: { key: ExecutionFilters["segment"]; label: string }[] = [
    { key: "all", label: "All" },
    { key: "attention", label: "Needs attention" },
    { key: "running", label: "Running" },
    { key: "completed", label: "Completed" },
    { key: "archived", label: "Archived" },
  ];

  return (
    <>
      {/* Runs waiting on a person, surfaced above the list rather than found
          by reading it. Only shown when there are any — a zero here would be
          a permanent fixture that stops being read. */}
      {attentionCount > 0 && filters.segment !== "attention" && (
        <button
          type="button"
          className="exec-attention"
          onClick={() => setFilters({ ...filters, segment: "attention", page: 1 })}
        >
          <span className="status-dot is-failed" />
          <b>
            {attentionCount} run{attentionCount === 1 ? "" : "s"} need
            {attentionCount === 1 ? "s" : ""} attention
          </b>
          <span className="muted mini">failed, paused, or not published — show only these</span>
        </button>
      )}

      <div className="exec-filters">
        <div className="exec-segments" role="group" aria-label="Filter by status">
          {segments.map((seg) => (
            <button
              key={seg.key}
              type="button"
              className={filters.segment === seg.key ? "active" : ""}
              aria-pressed={filters.segment === seg.key}
              onClick={() => setFilters({ ...filters, segment: seg.key, page: 1 })}
            >
              {seg.label}
              {seg.key === "attention" && attentionCount > 0 && (
                <span className="exec-seg-count">{attentionCount}</span>
              )}
            </button>
          ))}
        </div>

        <input
          type="search"
          className="exec-search"
          placeholder="Search target, chapter, or pipeline…"
          aria-label="Search by target, chapter, or pipeline name"
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
        />

        <select
          aria-label="Time range"
          value={filters.range}
          onChange={(e) =>
            setFilters({ ...filters, range: e.target.value as ExecutionFilters["range"], page: 1 })
          }
        >
          <option value="today">Today</option>
          <option value="1d">Last 24 hours</option>
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="all">All time</option>
          <option value="custom">Custom range…</option>
        </select>

        {filters.range === "custom" && (
          <>
            <input
              type="date"
              aria-label="Custom range start"
              value={filters.customFrom ?? ""}
              max={filters.customTo ?? undefined}
              onChange={(e) => setFilters({ ...filters, customFrom: e.target.value || null, page: 1 })}
            />
            <input
              type="date"
              aria-label="Custom range end"
              value={filters.customTo ?? ""}
              min={filters.customFrom ?? undefined}
              onChange={(e) => setFilters({ ...filters, customTo: e.target.value || null, page: 1 })}
            />
          </>
        )}

        {/* Shown whenever there is anything to show, even a single option:
            the dropdown is also how an operator learns which collections are
            represented, and a control that appears only once a second
            collection exists reads as the page being broken until then. */}
        {collectionOptions.length > 0 && (
          <select
            aria-label="Collection"
            value={filters.collectionId ?? ""}
            onChange={(e) =>
              // The pipeline selection is cleared alongside: a pipeline belongs
              // to one collection, so keeping it would leave both filters
              // active over an empty list with no obvious cause.
              setFilters({
                ...filters,
                collectionId: e.target.value ? Number(e.target.value) : null,
                pipelineId: null,
                page: 1,
              })
            }
          >
            <option value="">All collections</option>
            {collectionOptions.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.name} ({opt.count})
              </option>
            ))}
          </select>
        )}

        {pipelineOptions.length > 0 && (
          <select
            aria-label="Pipeline"
            value={filters.pipelineId ?? ""}
            onChange={(e) =>
              setFilters({
                ...filters,
                pipelineId: e.target.value ? Number(e.target.value) : null,
                page: 1,
              })
            }
          >
            <option value="">All pipelines</option>
            {pipelineOptions.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.name} ({opt.count})
              </option>
            ))}
          </select>
        )}

        {chapterOptions.length > 0 && (
          <select
            aria-label="Chapter"
            title="Filter by the target's chapter"
            value={filters.chapterId ?? ""}
            onChange={(e) =>
              setFilters({
                ...filters,
                chapterId: e.target.value || null,
                page: 1,
              })
            }
          >
            <option value="">All chapters</option>
            {chapterOptions.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.name} ({opt.count})
              </option>
            ))}
          </select>
        )}
        <select
          aria-label="Reviewer status"
          title="Filter by the human reviewer's verdict"
          value={filters.reviewStatus ?? ""}
          onChange={(e) =>
            setFilters({
              ...filters,
              reviewStatus: isReviewStatus(e.target.value) ? e.target.value : null,
              page: 1,
            })
          }
        >
          <option value="">Any review status</option>
          {ALL_REVIEW_STATUSES.map((st) => (
            <option key={st} value={st}>
              {REVIEW_STATUS_META[st].label}
            </option>
          ))}
        </select>
        <select
          aria-label="Approval status"
          title="Filter by whether the content was actually approved and pushed"
          value={filters.approvalStatus ?? ""}
          onChange={(e) =>
            setFilters({
              ...filters,
              approvalStatus:
                e.target.value === "approved" || e.target.value === "not_approved" ? e.target.value : null,
              page: 1,
            })
          }
        >
          <option value="">Any approval status</option>
          <option value="approved">Approved</option>
          <option value="not_approved">Not approved</option>
        </select>
        <select
          aria-label="Sort by"
          value={`${filters.sort}:${filters.direction}`}
          onChange={(e) => {
            const [sort, direction] = e.target.value.split(":");
            setFilters({
              ...filters,
              sort: sort as ExecutionFilters["sort"],
              direction: direction as ExecutionFilters["direction"],
              page: 1,
            });
          }}
        >
          <option value="createdAt:desc">Newest first</option>
          <option value="createdAt:asc">Oldest first</option>
          <option value="completedAt:desc">Recently finished</option>
          <option value="duration:desc">Slowest first</option>
          <option value="cost:desc">Most expensive</option>
          <option value="status:asc">Status</option>
        </select>
      </div>

      <RefetchOverlay loading={executions.loading && executions.data !== null}>
      {visible.length === 0 ? (
        <p className="muted" style={{ marginTop: 16 }}>
          No executions match these filters.{" "}
          <button
            type="button"
            className="btn mini"
            onClick={() =>
              setFilters({
                ...filters,
                segment: "all",
                search: "",
                range: "all",
                customFrom: null,
                customTo: null,
                collectionId: null,
                pipelineId: null,
                chapterId: null,
                page: 1,
              })
            }
          >
            Clear filters
          </button>
        </p>
      ) : (
    <div className="queue-list" style={{ marginTop: 12 }}>
      {selectedExecIds.size > 0 && (
        <div
          className="row between"
          style={{
            padding: "10px 16px",
            marginBottom: 12,
            background: "#eef2ff",
            border: "1px solid #6366f1",
            borderRadius: 8,
            alignItems: "center",
          }}
        >
          <div className="row" style={{ gap: 12, alignItems: "center" }}>
            <span style={{ fontWeight: 600, color: "#3730a3" }}>
              {selectedExecIds.size} execution{selectedExecIds.size === 1 ? "" : "s"} selected
            </span>
            <button
              type="button"
              className="btn mini"
              onClick={() => setSelectedExecIds(new Set())}
            >
              Clear Selection
            </button>
          </div>
          <ActionButton
            className="mini primary"
            pending={isPending("bulk-archive")}
            onClick={async () => {
              try {
                const res = await run("bulk-archive", () =>
                  api.post<{ archivedCount: number }>("/pipelines/executions/bulk-archive", {
                    ids: Array.from(selectedExecIds),
                  }),
                );
                toast(`Archived ${res.archivedCount} execution${res.archivedCount === 1 ? "" : "s"}`);
                setSelectedExecIds(new Set());
                setLocalRefreshKey((k) => k + 1);
              } catch (err: any) {
                toast(err.message || "Failed to archive executions");
              }
            }}
          >
            📦 Archive Selected ({selectedExecIds.size})
          </ActionButton>
        </div>
      )}

      {filters.segment !== "archived" && visible.some((e) => isArchivable(e.status)) && (
        <div className="row" style={{ gap: 8, marginBottom: 10, alignItems: "center", fontSize: "0.85rem", paddingLeft: 4 }}>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", userSelect: "none" }}>
            <input
              type="checkbox"
              checked={
                visible.filter((e) => isArchivable(e.status)).length > 0 &&
                visible.filter((e) => isArchivable(e.status)).every((e) => selectedExecIds.has(e.id))
              }
              onChange={(e) => {
                const archivable = visible.filter((item) => isArchivable(item.status));
                if (e.target.checked) {
                  setSelectedExecIds(new Set(archivable.map((item) => item.id)));
                } else {
                  setSelectedExecIds(new Set());
                }
              }}
            />
            <span className="muted">Select all finished executions on this page</span>
          </label>
        </div>
      )}

      {visible.map((exec) => {
        // Counts and the output preview come from the server's summary: the
        // list no longer ships the step graph, which is fetched only when a
        // row is expanded. The server resolves the designated output step by
        // the same shared rule, so the Content button still previews the step
        // that gets published rather than a trailing review's commentary.
        const detail = stepDetail[exec.id];
        const steps = detail?.stepExecutions ?? exec.stepExecutions;
        const completedStepsCount =
          steps?.filter((s: any) => s.status === "COMPLETED").length ??
          exec.stepSummary?.completed ??
          0;
        const totalStepsCount = steps?.length ?? exec.stepSummary?.total ?? 0;
        const finalContentText = steps
          ? finalOutputStep(steps, (exec.pipelineSnapshot as any)?.steps ?? [])?.outputText ?? null
          : exec.stepSummary?.outputText ?? null;

        const execIsProcessing = exec.status === "PROCESSING";
        const execLiveElapsed = execIsProcessing && exec.startedAt ? Math.max(0, (now - new Date(exec.startedAt).getTime()) / 1000) : null;

        const isApproved = exec.status === "APPROVED" || exec.generatedContent?.status === "APPROVED";
        const isAutoApproved = Boolean(exec.generatedContent?.autoApproved || exec.pipeline?.autoApprove);
        const approvedAt = exec.generatedContent?.updatedAt && isApproved ? new Date(exec.generatedContent.updatedAt).toLocaleString() : null;
        const approvedByUser = exec.generatedContent?.approvedByUserName ?? null;
        const reviewStatus: ReviewStatus = isReviewStatus(exec.reviewStatus ?? "")
          ? (exec.reviewStatus as ReviewStatus)
          : "PENDING";
        const pushStatus = exec.generatedContent?.pushStatus ?? null;
        const pushError = exec.generatedContent?.pushError ?? null;
        const externalContentId = exec.generatedContent?.externalContentId ?? null;

        return (
          <div
            key={exec.id}
            className={`book exec-row is-${severityOf(exec)}`}
            style={{ padding: 14, marginBottom: 10, borderRadius: 8 }}
          >
            <div className="row between" style={{ alignItems: "flex-start", cursor: "pointer" }} onClick={() => setExpandedId(expandedId === exec.id ? null : exec.id)}>
              <div className="row" style={{ gap: 10, alignItems: "flex-start" }}>
                {filters.segment !== "archived" && (
                  <input
                    type="checkbox"
                    disabled={!isArchivable(exec.status)}
                    checked={selectedExecIds.has(exec.id)}
                    title={isArchivable(exec.status) ? "Select for bulk archive" : "Only finished executions can be archived"}
                    style={{ marginTop: 5, cursor: isArchivable(exec.status) ? "pointer" : "not-allowed" }}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      e.stopPropagation();
                      const next = new Set(selectedExecIds);
                      if (next.has(exec.id)) next.delete(exec.id);
                      else next.add(exec.id);
                      setSelectedExecIds(next);
                    }}
                  />
                )}
                {(() => {
                  const pipelineModel =
                    exec.pipeline?.defaultModel ??
                    (exec.pipelineSnapshot as any)?.defaultModel ??
                    null;
                  return (
                    <div>
                      {/* Pipeline and topic share the headline, as a chip and a
                        title rather than two competing strings. The pipeline name
                        repeats down the whole list, so it cannot be the title —
                        that gave every row the same headline and buried the topic,
                        which is what actually distinguishes one run from another.
                        Reading it as a chip keeps the topic the thing being named
                        while still making the pipeline impossible to miss. */}
                      <div className="row" style={{ gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                        <span className="exec-pipeline">
                          {exec.pipeline?.name ?? `Pipeline #${exec.id}`}
                        </span>
                        {pipelineModel && (
                          <span className="badge pending mini" title="Pipeline default model">
                            🤖 {pipelineModel}
                          </span>
                        )}
                        <span className="exec-target">
                          {exec.targetName ?? exec.topicId ?? exec.subChapterId ?? "Untitled target"}
                        </span>
                        <span className={`badge ${isApproved ? "approved" : exec.status === "COMPLETED" ? "generated" : exec.status === "FAILED" ? "failed" : "pending"}`}>
                          {isApproved ? "APPROVED" : exec.status}
                        </span>
                        {isApproved && (
                          <span className="badge info mini">
                            {isAutoApproved ? "Auto-approved" : "Approved"}
                          </span>
                        )}
                        {pushStatus === "PUSHED" && (
                          <span className="badge generated mini" title={`Pushed to the main app (ID: ${externalContentId})`}>
                            Pushed #{externalContentId}
                          </span>
                        )}
                        {pushStatus === "FAILED" && (
                          <span className="badge failed mini" title={pushError ?? "Push failed"}>
                            Not published
                          </span>
                        )}
                        {/* The reviewer's verdict, separate from the run's own
                            status: advisory input for whoever approves. PENDING
                            is the common case and would be noise on every row. */}
                        {reviewStatus !== "PENDING" && (
                          <span
                            className={`badge ${REVIEW_STATUS_META[reviewStatus].badge} mini`}
                            title={`${REVIEW_STATUS_META[reviewStatus].hint}${
                              exec.reviewedByUserName ? ` — ${exec.reviewedByUserName}` : ""
                            }`}
                          >
                            👤 {REVIEW_STATUS_META[reviewStatus].label}
                          </span>
                        )}
                      </div>
                      <div className="muted mini" style={{ marginTop: 4 }}>
                        v{exec.pipelineVersion} ·{" "}
                        {pipelineModel ? <>🤖 {pipelineModel} · </> : null}
                        {completedStepsCount}/{totalStepsCount} steps ·{" "}
                        {new Date(exec.createdAt).toLocaleString()}
                        {exec.triggeredByUserName && ` · by ${exec.triggeredByUserName}`}
                      </div>
                    </div>
                  );
                })()}
              </div>

            <div className="row" style={{ gap: 10, alignItems: "center" }}>
                <span className="mini muted">
                  {compactTokens(exec.totalInputTokens)} in / {compactTokens(exec.totalOutputTokens)} out · {formatCost(exec.totalEstimatedCost)} ·{" "}
                  {execIsProcessing && execLiveElapsed !== null ? (
                    <span className="badge pending mini" style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                      ⏱️ {formatDuration(execLiveElapsed * 1000)} (live)
                    </span>
                  ) : (
                    formatDuration(exec.totalDurationMs)
                  )}
                </span>
                {finalContentText && (
                  <button
                    type="button"
                    className="btn mini primary"
                    onClick={async (e) => {
                      e.stopPropagation();
                      const base = {
                        // Target first here too, so the modal heading matches
                        // what the row is labelled by.
                        title: `${exec.targetName ?? `Target #${exec.id}`} — ${exec.pipeline?.name ?? "Pipeline"}`,
                        content: finalContentText,
                        responseType: exec.pipelineSnapshot?.responseType ?? "html",
                        // The PDFs this run was actually grounded on, so the
                        // reviewer checks the content against its own source
                        // rather than whatever the collection holds today.
                        collectionId: exec.collectionId ?? null,
                        sources: (exec.knowledgeSources ?? []) as ContentSource[],
                      };
                      // exec.generatedContent comes from the executions list
                      // fetch, which is not refetched on every click — after
                      // an edit saved from inside this same modal, reopening
                      // it from the row would otherwise show the value the
                      // list had when the page loaded rather than what was
                      // just saved. A fresh id is enough to open with; the
                      // fetch below (mirroring the Generation Queue) fills in
                      // the current generatedContent/reviewedContent.
                      const contentId = exec.generatedContent?.id;
                      setViewContentModal({ ...base, contentId });
                      setIsContentFullScreen(true);
                      if (contentId) {
                        try {
                          const fresh = await api.get<any>(`/generated-contents/${contentId}`);
                          setViewContentModal((m) =>
                            m && m.contentId === contentId
                              ? {
                                  ...m,
                                  generatedContent: fresh.generatedContent,
                                  reviewedContent: fresh.reviewedContent ?? null,
                                }
                              : m,
                          );
                        } catch {
                          // Falls back to whatever the list had; ContentReviewSection
                          // itself also fetches the row for the reviewer-status panel.
                        }
                      }
                    }}
                  >
                    Content
                  </button>
                )}
                {(exec.status === "PENDING" || exec.status === "PROCESSING") && (
                  <ActionButton
                    className="mini warning"
                    pending={isPending(`exec-${exec.id}-pause`)}
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        await run(`exec-${exec.id}-pause`, () =>
                          api.post(`/pipelines/executions/${exec.id}/pause`, {}),
                        );
                        toast("Pipeline execution paused");
                        setLocalRefreshKey((k) => k + 1);
                      } catch (err: any) {
                        toast(err.message || "Failed to pause execution");
                      }
                    }}
                  >
                    ⏸ Pause
                  </ActionButton>
                )}
                {exec.status === "PAUSED" && (
                  <ActionButton
                    className="mini primary"
                    pending={isPending(`exec-${exec.id}-resume`)}
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        await run(`exec-${exec.id}-resume`, () =>
                          api.post(`/pipelines/executions/${exec.id}/resume`, {}),
                        );
                        toast("Pipeline execution resumed");
                        setLocalRefreshKey((k) => k + 1);
                      } catch (err: any) {
                        toast(err.message || "Failed to resume execution");
                      }
                    }}
                  >
                    ▶ Resume
                  </ActionButton>
                )}
                {(exec.status === "PENDING" || exec.status === "PROCESSING" || exec.status === "PAUSED") && (
                  <ActionButton
                    className="mini danger"
                    pending={isPending(`exec-${exec.id}-cancel`)}
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        await run(`exec-${exec.id}-cancel`, () =>
                          api.post(`/pipelines/executions/${exec.id}/cancel`, {}),
                        );
                        toast("Pipeline execution cancelled");
                        setLocalRefreshKey((k) => k + 1);
                      } catch (err: any) {
                        toast(err.message || "Failed to cancel execution");
                      }
                    }}
                  >
                    ✕ Cancel
                  </ActionButton>
                )}
                {exec.status === "COMPLETED" && exec.generatedContentId && (
                  <ActionButton
                    className="mini primary"
                    pending={isPending(`exec-${exec.id}-approve`)}
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        await run(`exec-${exec.id}-approve`, () =>
                          api.post(`/generated-contents/${exec.generatedContentId}/approve`, {}),
                        );
                        toast("Pipeline output approved & pushed to main app!");
                        setLocalRefreshKey((k) => k + 1);
                      } catch (err: any) {
                        toast(err.message || "Failed to approve content");
                      }
                    }}
                  >
                    ✅ Approve & Push
                  </ActionButton>
                )}
                {filters.segment === "archived" ? (
                  <ActionButton
                    className="mini"
                    pending={isPending(`exec-${exec.id}-unarchive`)}
                    title="Restore to active executions"
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        await run(`exec-${exec.id}-unarchive`, () =>
                          api.post(`/pipelines/executions/${exec.id}/unarchive`, {}),
                        );
                        toast("Pipeline execution restored from archive");
                        setLocalRefreshKey((k) => k + 1);
                      } catch (err: any) {
                        toast(err.message || "Failed to unarchive execution");
                      }
                    }}
                  >
                    📤 Unarchive
                  </ActionButton>
                ) : isArchivable(exec.status) && (
                  <ActionButton
                    className="mini secondary"
                    pending={isPending(`exec-${exec.id}-archive`)}
                    title="Move this finished run to Archive"
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        await run(`exec-${exec.id}-archive`, () =>
                          api.post(`/pipelines/executions/${exec.id}/archive`, {}),
                        );
                        toast("Pipeline execution archived");
                        setLocalRefreshKey((k) => k + 1);
                      } catch (err: any) {
                        toast(err.message || "Failed to archive execution");
                      }
                    }}
                  >
                    📦 Archive
                  </ActionButton>
                )}
                <button
                  type="button"
                  className="btn mini"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandedId(expandedId === exec.id ? null : exec.id);
                  }}
                >
                  {expandedId === exec.id ? "Hide Details" : "Inspect Steps"}
                </button>
              </div>
            </div>

            {expandedId === exec.id && (
              <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--border)" }}>
                {/* The failure leads. It was previously the last thing on the
                    screen, below a summary of zeros — an operator opening a
                    failed run had to scroll past every metric to reach the one
                    line saying what went wrong. */}
                {exec.errorMessage && (
                  <div className="notice error" style={{ marginBottom: 14 }}>
                    <b>Failed:</b> {exec.errorMessage}
                  </div>
                )}

                {exec.generatedContentId && (
                  <div className="notice success" style={{ marginBottom: 14 }}>
                    Final content ready — <b>#{exec.generatedContentId}</b>, available in Content Review.
                  </div>
                )}

                <ReviewControl
                  kind="pipeline-execution"
                  exec={exec}
                  toast={toast}
                  onChanged={() => setLocalRefreshKey((k) => k + 1)}
                />

                {/* One line of what an operator scans for, with the rest a
                    click away. Target, progress and created-at are not
                    repeated: they are already in the row header above. */}
                <div className="exec-summary">
                  <span>
                    <b>{completedStepsCount}/{totalStepsCount}</b> steps
                  </span>
                  <span>
                    {execIsProcessing && execLiveElapsed !== null
                      ? `${formatDuration(execLiveElapsed * 1000)} (live)`
                      : formatDuration(exec.totalDurationMs)}
                  </span>
                  {exec.totalEstimatedCost > 0 && <span>{formatCost(exec.totalEstimatedCost)}</span>}
                  {pushStatus === "PUSHED" && (
                    <span className="badge generated mini">Pushed #{externalContentId}</span>
                  )}
                  {pushStatus === "FAILED" && (
                    <span className="badge failed mini" title={pushError ?? undefined}>
                      Not published
                    </span>
                  )}
                  {isApproved && (
                    <span className="badge approved mini">
                      {isAutoApproved ? "Auto-approved" : "Approved"}
                    </span>
                  )}

                  <InfoPopover label="Execution details">
                    <b className="info-pop-title">Execution details</b>
                    <table className="info-pop-table">
                      <tbody>
                    <Row label="Pipeline" value={exec.pipeline?.name ?? `Pipeline #${exec.id}`} />
                    <Row
                      label="Pipeline Model"
                      value={
                        (exec.pipeline?.defaultModel ?? (exec.pipelineSnapshot as any)?.defaultModel) ? (
                          <code>{exec.pipeline?.defaultModel ?? (exec.pipelineSnapshot as any)?.defaultModel}</code>
                        ) : (
                          <span className="muted">Inherited from collection</span>
                        )
                      }
                    />
                    {exec.requestedModel && (
                      <Row label="Run Model" value={<code>{exec.requestedModel}</code>} />
                    )}
                    <Row label="Target" value={exec.targetName ?? exec.topicId ?? exec.subChapterId ?? "—"} />
                    <Row label="Created" value={new Date(exec.createdAt).toLocaleString()} />
                    <Row
                      label="Triggered by"
                      value={
                        exec.triggeredByUserName
                          ? `${exec.triggeredByUserName}${
                              exec.triggeredByUserRole ? ` (${exec.triggeredByUserRole})` : ""
                            }`
                          : "System"
                      }
                    />
                    <Row
                      label="Tokens"
                      value={
                        exec.totalInputTokens || exec.totalOutputTokens
                          ? `${compactTokens(exec.totalInputTokens)} in / ${compactTokens(exec.totalOutputTokens)} out${
                              exec.totalReasoningTokens
                                ? ` (${compactTokens(exec.totalReasoningTokens)} reasoning)`
                                : ""
                            }`
                          : "—"
                      }
                    />
                    <Row label="Cost" value={formatCost(exec.totalEstimatedCost)} />
                    {approvedAt && (
                      <Row
                        label="Approved"
                        value={`${approvedAt}${approvedByUser ? ` by ${approvedByUser}` : ""}`}
                      />
                    )}
                    {pushStatus === "FAILED" && pushError && (
                      <Row label="Push error" value={pushError} />
                    )}
                    {exec.knowledgeSources && exec.knowledgeSources.length > 0 && (
                      <Row
                        label={`Knowledge PDFs (${exec.knowledgeSources.length})`}
                        value={exec.knowledgeSources.map((ks: any) => ks.filename).join(", ")}
                      />
                    )}
                      </tbody>
                    </table>
                  </InfoPopover>
                </div>

                <h4 className="exec-steps-head">Steps</h4>
                {!steps && detailLoading === exec.id && (
                  <p className="muted mini">Loading steps…</p>
                )}
                <table>
                  <thead>
                    {/* Model, tokens and cost were dropped: an operator
                        expanding a row is asking which step broke, not what it
                        cost. All three remain in the step inspector. */}
                    <tr>
                      <th>Step</th>
                      <th>Status</th>
                      <th>Duration</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {steps?.map((se: any) => {
                      const stepIsProcessing = se.status === "PROCESSING";
                      const stepLiveElapsed = stepIsProcessing && se.startedAt ? Math.max(0, (now - new Date(se.startedAt).getTime()) / 1000) : null;

                      return (
                        <tr key={se.id}>
                          {/* Step number and name share a cell: they are one
                              label, and splitting them cost a column while
                              making neither easier to read. */}
                          <td>
                            <b>
                              {se.stepOrder}. {se.stepName}
                            </b>
                            <div className="exec-step-tags">
                              {se.isManualRerun && (
                                <span
                                  className="queue-tag"
                                  title={
                                    se.correctedAt
                                      ? `Corrected ${new Date(se.correctedAt).toLocaleString()}`
                                      : undefined
                                  }
                                >
                                  Corrected
                                  {se.correctedByUserName ? ` by ${se.correctedByUserName}` : ""}
                                </span>
                              )}
                              {se.stepSnapshot?.promptDocFilename && (
                                <span
                                  className="queue-tag"
                                  style={{ cursor: "pointer" }}
                                  title={`Click to view ${se.stepSnapshot.promptDocFilename}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setViewingDocx({
                                      filename: se.stepSnapshot.promptDocFilename,
                                      fileUrl: `/api/pipelines/prompt-doc/${se.stepSnapshot.promptDocFileId}/file?filename=${encodeURIComponent(se.stepSnapshot.promptDocFilename)}`,
                                      subtitle: `Step: ${se.stepName || "Prompt Document"}`,
                                    });
                                  }}
                                >
                                  📄 {se.stepSnapshot.promptDocFilename}
                                </span>
                              )}
                              {se.stepSnapshot?.enableRetrieval && <span className="queue-tag">Retrieval</span>}
                              {se.stepType === "INTERNAL_TRANSFORM" || se.model === "internal-transform" ? (
                                <span className="queue-tag">Internal transform</span>
                              ) : (
                                <span className="queue-model">{se.model ?? se.requestedModel ?? "—"}</span>
                              )}
                            </div>
                            {se.errorMessage && (
                              <div className="exec-step-error">{se.errorMessage}</div>
                            )}
                          </td>
                          <td>
                            <span className={`badge ${se.status === "COMPLETED" ? "generated" : se.status === "FAILED" ? "failed" : "pending"}`}>
                              {se.status}
                            </span>
                            {(se.retryLogs?.length > 0 || se.attemptCount > 1) && (
                              <span className="badge failed mini" style={{ marginLeft: 4 }} title={`${se.retryLogs?.length ?? se.attemptCount} attempt(s)`}>
                                {se.attemptCount}&times;
                              </span>
                            )}
                          </td>
                          <td>
                            {stepIsProcessing && stepLiveElapsed !== null ? (
                              <span className="badge pending mini" style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                                {formatDuration(stepLiveElapsed * 1000)}
                              </span>
                            ) : se.durationMs !== null ? (
                              formatDuration(se.durationMs)
                            ) : (
                              "—"
                            )}
                          </td>
                          <td>
                            <div className="row" style={{ gap: 6 }}>
                              <button
                                type="button"
                                className="btn mini"
                                onClick={() => setInspectStep(se)}
                                title="Inspect full stored step details"
                              >
                                Details
                              </button>
                              <button
                                type="button"
                                className="btn mini warning"
                                onClick={() => setRerunModalStep({ execId: exec.id, stepExec: se })}
                                title="Re-run step with manual input/prompt corrections"
                              >
                                Rerun
                              </button>
                              {se.status === "COMPLETED" && se.outputText && (
                                <button
                                  type="button"
                                  className="btn mini primary"
                                  onClick={() => {
                                    setViewContentModal({
                                      title: `Step ${se.stepOrder}: ${se.stepName} Output`,
                                      content: se.outputText,
                                      responseType: se.stepSnapshot?.responseType ?? "html",
                                      // Per-step output is reviewed on its own:
                                      // an intermediate step's text is not what
                                      // gets published, so it is not paired with
                                      // the source PDF the way the run's final
                                      // content is.
                                      collectionId: null,
                                      sources: [],
                                    });
                                    setIsContentFullScreen(true);
                                  }}
                                >
                                  👁 View Output
                                </button>
                              )}
                              {se.status === "FAILED" && (
                                <ActionButton
                                  className="mini danger"
                                  pending={isPending(`retry-step-${se.id}`)}
                                  onClick={() => void retryStep(exec.id, se.id)}
                                >
                                  Retry Step
                                </ActionButton>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

              </div>
            )}
          </div>
        );
      })}
    </div>
      )}

      {/* The pager is rendered whenever there is more than one page, below the
          rows rather than above: the count belongs next to what it counts, and
          an operator scanning the list reaches it having already read them. */}
      
        <div className="exec-pager row between">
          <div className="row" style={{ gap: 10, alignItems: "center" }}>
            <span className="muted mini">
              {total === 0
                ? "No runs"
                : `${(executions.data!.page - 1) * executions.data!.pageSize + 1}–${Math.min(
                    executions.data!.page * executions.data!.pageSize,
                    total,
                  )} of ${total}`}
            </span>
            <label className="exec-pagesize muted mini">
              Rows
              <select
                aria-label="Rows per page"
                value={filters.pageSize}
                onChange={(e) => {
                  const pageSize = Number(e.target.value);
                  // Back to the first page: the row the operator was looking at
                  // sits on a different page under a different size, so keeping
                  // the number would land them somewhere unrelated — or past the
                  // end of a list that just got shorter.
                  setFilters({ ...filters, pageSize, page: 1 });
                }}
              >
                {PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="row" style={{ gap: 6, alignItems: "center" }}>
            <button
              type="button"
              className="btn mini"
              disabled={filters.page <= 1 || executions.loading}
              onClick={() => setFilters({ ...filters, page: Math.max(1, filters.page - 1) })}
            >
              ‹ Previous
            </button>
            <span className="muted mini">
              Page {executions.data!.page} of {pageCount}
            </span>
            <button
              type="button"
              className="btn mini"
              disabled={filters.page >= pageCount || executions.loading}
              onClick={() =>
                setFilters({ ...filters, page: Math.min(pageCount, filters.page + 1) })
              }
            >
              Next ›
            </button>
          </div>
        </div>
      </RefetchOverlay>

      {/* Manual Step Rerun Modal */}
      {rerunModalStep && (
        <ManualStepCorrectionModal
          execId={rerunModalStep.execId}
          stepExec={rerunModalStep.stepExec}
          onClose={() => setRerunModalStep(null)}
          onSubmitted={() => setLocalRefreshKey((k) => k + 1)}
          toast={toast}
        />
      )}

      {/* Final Content Preview Modal */}
      {viewContentModal && (
        <div
          className={`modal-backdrop sourced-content-backdrop ${isContentFullScreen ? "viewer-fullscreen-backdrop" : ""}`}
          onClick={() => {
            setViewContentModal(null);
            setIsContentFullScreen(false);
          }}
        >
          <div
            className={`modal-card sourced-content-card ${isContentFullScreen ? "viewer-fullscreen-card" : ""}`}
            style={{
              maxWidth: isContentFullScreen ? undefined : viewContentModal.sources.length > 0 ? 1500 : 880,
              width: "95%",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="row between mb-4" style={{ marginBottom: 16, flexShrink: 0 }}>
              <h3 style={{ margin: 0 }}>{viewContentModal.title}</h3>
              <div className="row" style={{ gap: 8 }}>
                <button
                  type="button"
                  className="btn mini"
                  onClick={() => {
                    void navigator.clipboard.writeText(viewContentModal.content);
                    toast("Copied to clipboard!");
                  }}
                >
                  📋 Copy
                </button>
                {viewContentModal.sources.length > 0 && (
                  <button
                    type="button"
                    className="btn mini"
                    onClick={() => setIsContentFullScreen((v) => !v)}
                    title={isContentFullScreen ? "Exit Fullscreen" : "Fullscreen — more room for both panes"}
                  >
                    {isContentFullScreen ? "⤡ Exit Fullscreen" : "⛶ Fullscreen"}
                  </button>
                )}
                <button
                  type="button"
                  className="btn mini"
                  onClick={() => {
                    setViewContentModal(null);
                    setIsContentFullScreen(false);
                  }}
                >
                  ✕ Close
                </button>
              </div>
            </div>
            <SourcedContentView
              collectionId={viewContentModal.collectionId}
              sources={viewContentModal.sources}
              content={viewContentModal.content}
              responseType={viewContentModal.responseType ?? "html"}
              review={
                viewContentModal.contentId
                  ? {
                      contentId: viewContentModal.contentId,
                      generatedContent: viewContentModal.generatedContent ?? viewContentModal.content,
                      reviewedContent: viewContentModal.reviewedContent ?? null,
                      toast,
                      onSaved: (reviewedContent) => {
                        setViewContentModal((m) =>
                          m
                            ? {
                                ...m,
                                reviewedContent,
                                content: reviewedContent ?? m.generatedContent ?? m.content,
                              }
                            : m,
                        );
                      },
                      onApproved: () => setLocalRefreshKey((k) => k + 1),
                    }
                  : undefined
              }
            />
          </div>
        </div>
      )}

      {/* Complete Step Details Modal */}
      {inspectStep && (
        <div className="modal-backdrop" onClick={() => setInspectStep(null)}>
          <div className="modal-card" style={{ maxWidth: 880, width: "90%", maxHeight: "90vh", overflowY: "auto" }} onClick={(e) => e.stopPropagation()}>
            <div className="row between mb-4" style={{ marginBottom: 16 }}>
              <h3 style={{ margin: 0 }}>
                Step #{inspectStep.stepOrder} Full Details: {inspectStep.stepName}
              </h3>
              <div className="row" style={{ gap: 8 }}>
                <button
                  type="button"
                  className="btn mini warning"
                  onClick={() => {
                    const execId = inspectStep.pipelineExecutionId;
                    const se = inspectStep;
                    setInspectStep(null);
                    setRerunModalStep({ execId, stepExec: se });
                  }}
                >
                  ✏️ Rerun with Corrections
                </button>
                <button type="button" className="btn mini" onClick={() => setInspectStep(null)}>
                  ✕ Close
                </button>
              </div>
            </div>

            {inspectStep.errorMessage && (
              <div className="notice error" style={{ marginBottom: 14 }}>
                <h4 style={{ margin: "0 0 4px 0", color: "var(--red)" }}>⚠️ Step Execution Error</h4>
                <div style={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}>{inspectStep.errorMessage}</div>
              </div>
            )}

            <div className="book" style={{ padding: 14, borderRadius: 6, marginBottom: 14, background: "rgba(0,0,0,0.02)" }}>
              <h4 style={{ margin: "0 0 8px 0" }}>📊 General Telemetry & Execution Info</h4>
              <table>
                <tbody>
                  <Row label="Step ID" value={`#${inspectStep.id}`} />
                  <Row label="Step Order" value={`Step ${inspectStep.stepOrder}`} />
                  <Row label="Step Name" value={inspectStep.stepName} />
                  <Row label="Step Type" value={<code>{inspectStep.stepType ?? (inspectStep.stepSnapshot?.stepType as string) ?? "LLM"}</code>} />
                  <Row label="Status" value={<span className={`badge ${inspectStep.status === "COMPLETED" ? "generated" : inspectStep.status === "FAILED" ? "failed" : "pending"}`}>{inspectStep.status}</span>} />
                  <Row label="Attempt Count" value={`${inspectStep.attemptCount ?? 1} attempt(s)`} />
                  {inspectStep.batchProviderId && <Row label="OpenAI Batch ID" value={<code>{inspectStep.batchProviderId}</code>} />}
                  {inspectStep.batchCustomId && <Row label="Batch Custom ID" value={<code>{inspectStep.batchCustomId}</code>} />}
                  <Row label="Model Requested" value={<code>{inspectStep.requestedModel ?? (inspectStep.stepSnapshot?.requestedModel as string) ?? "Default"}</code>} />
                  <Row label="Model Executed" value={<code>{inspectStep.model ?? "Pending / Default"}</code>} />
                  <Row label="Input Tokens" value={inspectStep.inputTokens !== null ? inspectStep.inputTokens.toLocaleString() : "—"} />
                  <Row label="Output Tokens" value={inspectStep.outputTokens !== null ? inspectStep.outputTokens.toLocaleString() : "—"} />
                  <Row label="Cached Tokens" value={inspectStep.cachedTokens ? inspectStep.cachedTokens.toLocaleString() : "0"} />
                  <Row label="Reasoning Tokens" value={inspectStep.reasoningTokens ? inspectStep.reasoningTokens.toLocaleString() : "0"} />
                  <Row label="Estimated Cost" value={formatCost(inspectStep.estimatedCost)} />
                  <Row label="Duration" value={inspectStep.status === "PROCESSING" && inspectStep.startedAt ? `${formatDuration(Math.max(0, now - new Date(inspectStep.startedAt).getTime()))} (live)` : formatDuration(inspectStep.durationMs)} />
                  {inspectStep.isManualRerun && (
                    <Row
                      label="Corrected by"
                      value={`${inspectStep.correctedByUserName ?? "Unknown user"}${
                        inspectStep.correctedAt
                          ? ` on ${new Date(inspectStep.correctedAt).toLocaleString()}`
                          : ""
                      }`}
                    />
                  )}
                  <Row label="Vector Retrieval" value={inspectStep.stepSnapshot?.enableRetrieval ? `Enabled (${inspectStep.retrievedChunks ?? 0} chunks retrieved, ${inspectStep.searchCalls ?? 0} search call(s))` : "Disabled"} />
                  {inspectStep.startedAt && <Row label="Started At" value={new Date(inspectStep.startedAt).toLocaleString()} />}
                  {inspectStep.completedAt && <Row label="Completed At" value={new Date(inspectStep.completedAt).toLocaleString()} />}
                </tbody>
              </table>
            </div>

            {/* Files & Knowledge Sources Section */}
            {(() => {
              const parentExec = executions.data?.rows?.find(
                (e: any) => e.id === inspectStep.pipelineExecutionId,
              );
              const parentKnowledgeSources: any[] = parentExec?.knowledgeSources ?? [];
              const promptDocName = inspectStep.stepSnapshot?.promptDocFilename ?? inspectStep.stepSnapshot?.promptDocFileId;
              const promptDocSize = inspectStep.stepSnapshot?.promptDocSizeBytes;

              return (
                <div className="book" style={{ padding: 14, borderRadius: 6, marginBottom: 14, background: "rgba(0,0,0,0.02)" }}>
                  <h4 style={{ margin: "0 0 8px 0" }}>📚 Files & Attached Documents Used</h4>
                  
                  {/* Step-Level Prompt Document (.docx) */}
                  {promptDocName ? (
                    <div style={{ marginBottom: 12, padding: "10px 12px", background: "#ffffff", border: "1px solid #e1e4e8", borderRadius: 6 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                        <div style={{ fontWeight: 600, fontSize: "0.88rem", color: "var(--primary)" }}>
                          📄 Attached Step Prompt Document (.docx)
                        </div>
                        {inspectStep.stepSnapshot?.promptDocFileId && (
                          <button
                            type="button"
                            className="btn mini"
                            title="View Word document"
                            onClick={() =>
                              setViewingDocx({
                                filename: String(promptDocName),
                                fileUrl: `/api/pipelines/prompt-doc/${inspectStep.stepSnapshot.promptDocFileId}/file?filename=${encodeURIComponent(String(promptDocName))}`,
                                subtitle: `Execution Step: ${inspectStep.stepName || "Prompt Document"}`,
                              })
                            }
                          >
                            👁 View Document
                          </button>
                        )}
                      </div>
                      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", fontSize: "0.85rem" }}>
                        <span><b>Filename:</b> {String(promptDocName)}</span>
                        {promptDocSize && (
                          <span className="mini muted">({(Number(promptDocSize) / 1024).toFixed(1)} KB)</span>
                        )}
                        {inspectStep.stepSnapshot?.promptDocFileId && (
                          <span className="badge pending mini">OpenAI File ID: {String(inspectStep.stepSnapshot.promptDocFileId)}</span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="mini muted" style={{ marginBottom: 10 }}>No step-level .docx prompt document attached to this step.</div>
                  )}

                  {/* Vector Store PDF Knowledge Sources */}
                  {inspectStep.stepSnapshot?.enableRetrieval ? (
                    <div>
                      <div style={{ fontWeight: 600, fontSize: "0.88rem", marginBottom: 4 }}>
                        📖 Textbook PDF Knowledge Sources (Vector Search Enabled)
                      </div>
                      <div className="mini muted" style={{ marginBottom: 8 }}>
                        Retrieved <b>{inspectStep.retrievedChunks ?? 0}</b> text chunk(s) across <b>{inspectStep.searchCalls ?? 0}</b> vector search call(s).
                      </div>

                      {parentKnowledgeSources.length > 0 ? (
                        <table>
                          <thead>
                            <tr>
                              <th>PDF Filename</th>
                              <th>Role Tag</th>
                              <th>File Size</th>
                              <th>Indexing Status</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {parentKnowledgeSources.map((ks: any) => (
                              <tr key={ks.id}>
                                <td><b>📄 {ks.filename}</b></td>
                                <td><span className="badge pending mini">{ks.roleTag ?? "TEXTBOOK"}</span></td>
                                <td>{ks.sizeBytes ? `${(ks.sizeBytes / (1024 * 1024)).toFixed(2)} MB` : "—"}</td>
                                <td><span className="badge generated mini">{ks.indexingStatus ?? "ready"}</span></td>
                                <td>
                                  <button
                                    type="button"
                                    className="btn mini"
                                    disabled={ks.indexingStatus !== "ready"}
                                    title={ks.indexingStatus !== "ready" ? "File not yet ready" : "View PDF"}
                                    onClick={() =>
                                      setViewingPdf({
                                        id: ks.id,
                                        collectionId: parentExec?.collectionId ?? ks.collectionId,
                                        filename: ks.filename,
                                        roleTag: ks.roleTag,
                                      })
                                    }
                                  >
                                    View
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <div className="mini muted">Vector search was active for this step. No specific PDF override specified; searched full collection vector store.</div>
                      )}
                    </div>
                  ) : (
                    <div className="mini muted">Vector search (textbook PDF retrieval) was disabled for this step.</div>
                  )}
                </div>
              );
            })()}

            {/* Model Hyperparameters & Configuration Settings */}
            <div className="book" style={{ padding: 14, borderRadius: 6, marginBottom: 14, background: "rgba(0,0,0,0.02)" }}>
              <h4 style={{ margin: "0 0 8px 0" }}>⚙️ Stored Model Hyperparameters & Settings</h4>
              <table>
                <tbody>
                  <Row label="Response Type" value={<code>{(inspectStep.stepSnapshot?.responseType as string) ?? "html"}</code>} />
                  <Row label="Reasoning Effort" value={inspectStep.extraParameters?.reasoningEffort ? <code>{String(inspectStep.extraParameters.reasoningEffort)}</code> : inspectStep.stepSnapshot?.extraParameters && (inspectStep.stepSnapshot.extraParameters as any).reasoningEffort ? <code>{String((inspectStep.stepSnapshot.extraParameters as any).reasoningEffort)}</code> : "Unset / Default"} />
                  <Row label="Temperature" value={inspectStep.extraParameters?.temperature !== undefined && inspectStep.extraParameters?.temperature !== null ? String(inspectStep.extraParameters.temperature) : inspectStep.stepSnapshot?.extraParameters && (inspectStep.stepSnapshot.extraParameters as any).temperature !== undefined ? String((inspectStep.stepSnapshot.extraParameters as any).temperature) : "Default (0.7)"} />
                  <Row label="Top P" value={inspectStep.extraParameters?.topP !== undefined && inspectStep.extraParameters?.topP !== null ? String(inspectStep.extraParameters.topP) : inspectStep.stepSnapshot?.extraParameters && (inspectStep.stepSnapshot.extraParameters as any).topP !== undefined ? String((inspectStep.stepSnapshot.extraParameters as any).topP) : "Default (1.0)"} />
                  <Row label="Frequency Penalty" value={inspectStep.extraParameters?.frequencyPenalty !== undefined && inspectStep.extraParameters?.frequencyPenalty !== null ? String(inspectStep.extraParameters.frequencyPenalty) : inspectStep.stepSnapshot?.extraParameters && (inspectStep.stepSnapshot.extraParameters as any).frequencyPenalty !== undefined ? String((inspectStep.stepSnapshot.extraParameters as any).frequencyPenalty) : "Default (0.0)"} />
                  <Row label="Presence Penalty" value={inspectStep.extraParameters?.presencePenalty !== undefined && inspectStep.extraParameters?.presencePenalty !== null ? String(inspectStep.extraParameters.presencePenalty) : inspectStep.stepSnapshot?.extraParameters && (inspectStep.stepSnapshot.extraParameters as any).presencePenalty !== undefined ? String((inspectStep.stepSnapshot.extraParameters as any).presencePenalty) : "Default (0.0)"} />
                  <Row label="Max Tokens" value={inspectStep.extraParameters?.maxTokens !== undefined && inspectStep.extraParameters?.maxTokens !== null ? String(inspectStep.extraParameters.maxTokens) : inspectStep.stepSnapshot?.extraParameters && (inspectStep.stepSnapshot.extraParameters as any).maxTokens !== undefined ? String((inspectStep.stepSnapshot.extraParameters as any).maxTokens) : "Unset / Default"} />
                  <Row label="Seed" value={inspectStep.extraParameters?.seed !== undefined && inspectStep.extraParameters?.seed !== null ? String(inspectStep.extraParameters.seed) : inspectStep.stepSnapshot?.extraParameters && (inspectStep.stepSnapshot.extraParameters as any).seed !== undefined ? String((inspectStep.stepSnapshot.extraParameters as any).seed) : "Unset"} />
                  <Row label="Sample Mode" value={<code>{(inspectStep.stepSnapshot?.sampleMode as string) ?? "none"}</code>} />
                </tbody>
              </table>
            </div>

            {/* Resolved Input Variables */}
            {inspectStep.resolvedInputs && Object.keys(inspectStep.resolvedInputs).length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <h4 style={{ margin: "0 0 6px 0" }}>📥 Resolved Input Variables & Context</h4>
                <pre style={{ padding: 10, borderRadius: 6, background: "#f5f7fa", fontSize: "0.85rem", whiteSpace: "pre-wrap", maxHeight: 200, overflowY: "auto" }}>
                  {JSON.stringify(inspectStep.resolvedInputs, null, 2)}
                </pre>
              </div>
            )}

            {/* Configured Input Mappings */}
            {inspectStep.stepSnapshot?.inputMappings && Array.isArray(inspectStep.stepSnapshot.inputMappings) && inspectStep.stepSnapshot.inputMappings.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <h4 style={{ margin: "0 0 6px 0" }}>🔗 Configured Input Mappings</h4>
                <table>
                  <thead>
                    <tr>
                      <th>Variable Name</th>
                      <th>Source Type</th>
                      <th>Step Order / Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(inspectStep.stepSnapshot.inputMappings as any[]).map((map, idx) => (
                      <tr key={idx}>
                        <td><code>{`{{${map.varName}}}`}</code></td>
                        <td><span className="badge pending mini">{map.sourceType}</span></td>
                        <td>{map.sourceType === "STEP_OUTPUT" ? `Output from Step ${map.stepOrder}` : map.sourceType === "STATIC_VALUE" ? `Static: "${map.staticValue}"` : "Target Context"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Output Schema & Sample Exemplar */}
            {inspectStep.stepSnapshot?.outputSchema && (
              <div style={{ marginBottom: 14 }}>
                <h4 style={{ margin: "0 0 6px 0" }}>📋 Configured JSON Output Schema</h4>
                <pre style={{ padding: 10, borderRadius: 6, background: "#f5f7fa", fontSize: "0.85rem", whiteSpace: "pre-wrap", maxHeight: 180, overflowY: "auto" }}>
                  {JSON.stringify(inspectStep.stepSnapshot.outputSchema, null, 2)}
                </pre>
              </div>
            )}
            {inspectStep.stepSnapshot?.sampleOutput && (
              <div style={{ marginBottom: 14 }}>
                <h4 style={{ margin: "0 0 6px 0" }}>📄 Sample Exemplar / Template Output</h4>
                <pre style={{ padding: 10, borderRadius: 6, background: "#f5f7fa", fontSize: "0.85rem", whiteSpace: "pre-wrap", maxHeight: 180, overflowY: "auto" }}>
                  {String(inspectStep.stepSnapshot.sampleOutput)}
                </pre>
              </div>
            )}

            {/* Manual Rerun Details */}
            {inspectStep.isManualRerun && (
              <div className="notice warning" style={{ marginBottom: 14 }}>
                <h4 style={{ margin: "0 0 6px 0" }}>✏️ Manual Corrections Applied</h4>
                {inspectStep.manualAdditionalInstructions && (
                  <div><b>Additional instructions:</b> {inspectStep.manualAdditionalInstructions}</div>
                )}
                {inspectStep.manualExcludePromptDoc && <div><b>Prompt document:</b> excluded from this run</div>}
                {inspectStep.manualExcludedInputs && inspectStep.manualExcludedInputs.length > 0 && (
                  <div><b>Excluded inputs:</b> {inspectStep.manualExcludedInputs.join(", ")}</div>
                )}
                {inspectStep.correctionNotes && (
                  <div><b>Reason/Notes:</b> {inspectStep.correctionNotes}</div>
                )}
                {inspectStep.correctedAt && (
                  <div className="mini muted" style={{ marginTop: 4 }}>
                    Corrected At: {new Date(inspectStep.correctedAt).toLocaleString()}
                  </div>
                )}
                {inspectStep.manualInputs && (
                  <div style={{ marginTop: 8 }}>
                    <b className="mini">Corrected Input Variables:</b>
                    <pre style={{ padding: 6, background: "rgba(0,0,0,0.03)", borderRadius: 4, fontSize: "0.8rem", whiteSpace: "pre-wrap" }}>
                      {JSON.stringify(inspectStep.manualInputs, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}

            {/* Retry Logs History Table */}
            {inspectStep.retryLogs && inspectStep.retryLogs.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <h4 style={{ margin: "0 0 8px 0", color: "var(--red)" }}>
                  ⚠️ Retry History & Failure Logs ({inspectStep.retryLogs.length} failed attempt{inspectStep.retryLogs.length === 1 ? "" : "s"})
                </h4>
                <table>
                  <thead>
                    <tr>
                      <th>Attempt #</th>
                      <th>Failed At</th>
                      <th>Error Message</th>
                      <th>Will Retry</th>
                      <th>Retry Delay</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inspectStep.retryLogs.map((log: any) => (
                      <tr key={log.id}>
                        <td><b>Attempt {log.attemptNumber}</b></td>
                        <td className="mini">{new Date(log.failedAt).toLocaleString()}</td>
                        <td>
                          <span style={{ color: "var(--red)", fontWeight: 500 }}>{log.errorMessage}</span>
                          {log.errorStack && (
                            <details style={{ marginTop: 4 }}>
                              <summary className="mini muted" style={{ cursor: "pointer" }}>Stack Trace</summary>
                              <pre style={{ fontSize: "0.75rem", whiteSpace: "pre-wrap", background: "#fff0f0", padding: 6, borderRadius: 4 }}>
                                {log.errorStack}
                              </pre>
                            </details>
                          )}
                        </td>
                        <td>{log.willRetry ? "Yes" : "No (Permanent)"}</td>
                        <td className="mini">{log.retryDelayMs ? `${(log.retryDelayMs / 1000).toFixed(1)}s` : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {inspectStep.errorMessage && (!inspectStep.retryLogs || inspectStep.retryLogs.length === 0) && (
              <div className="notice error" style={{ marginBottom: 14 }}>
                <b>Execution Error:</b> {inspectStep.errorMessage}
              </div>
            )}

            {inspectStep.stepSnapshot?.prompt && (
              <div style={{ marginBottom: 14 }}>
                <h4 style={{ margin: "0 0 6px 0" }}>System Prompt / Instructions</h4>
                <pre style={{ padding: 10, borderRadius: 6, background: "#f5f7fa", fontSize: "0.85rem", whiteSpace: "pre-wrap" }}>
                  {inspectStep.manualPromptOverride ?? inspectStep.stepSnapshot.prompt}
                </pre>
              </div>
            )}

            {inspectStep.outputText && (
              <div>
                <div className="row between" style={{ marginBottom: 6 }}>
                  <h4 style={{ margin: 0 }}>Step Output Text</h4>
                  <button
                    type="button"
                    className="btn mini"
                    onClick={() => {
                      void navigator.clipboard.writeText(inspectStep.outputText);
                      toast("Step output copied to clipboard!");
                    }}
                  >
                    📋 Copy Output
                  </button>
                </div>
                <ContentPreview content={inspectStep.outputText} responseType={inspectStep.stepSnapshot?.responseType ?? "html"} />
              </div>
            )}
          </div>
        </div>
      )}

      {viewingPdf && (
        <PdfViewerModal
          source={viewingPdf}
          onClose={() => setViewingPdf(null)}
        />
      )}

      {viewingDocx && (
        <DocxViewerModal
          filename={viewingDocx.filename}
          fileUrl={viewingDocx.fileUrl}
          subtitle={viewingDocx.subtitle}
          onClose={() => setViewingDocx(null)}
        />
      )}
    </>
  );
}

export function Queue() {
  const [refreshKey, setRefreshKey] = useState(0);
  // useToast returns { toast, element }: the callback AND the node that renders
  // the message. Passing the object as `toast` made every toast() call in the
  // list throw "toast is not a function" — silently, until a call sat in front
  // of work that mattered and took it down with it.
  const { toast, element: toastElement } = useToast();

  return (
    <div className="page" style={{ maxWidth: "100%", width: "100%", padding: "16px 24px" }}>
      <div className="row between" style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Generation Queue</h2>
        <button className="btn" onClick={() => setRefreshKey((k) => k + 1)}>
          ↻ Refresh
        </button>
      </div>

      <GenerationQueueList refreshKey={refreshKey} toast={toast} />
      {toastElement}
    </div>
  );
}

export function PipelineExecutions() {
  const [refreshKey, setRefreshKey] = useState(0);
  const { toast, element: toastElement } = useToast();

  return (
    <div className="page" style={{ maxWidth: "100%", width: "100%", padding: "16px 24px" }}>
      <div className="row between" style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Pipeline Executions</h2>
        <button className="btn" onClick={() => setRefreshKey((k) => k + 1)}>
          ↻ Refresh
        </button>
      </div>

      <PipelineExecutionsList refreshKey={refreshKey} toast={toast} />
      {toastElement}
    </div>
  );
}

